import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRosterAttendance } from "../../actions/RosterAttendanceAction";
import { fetchLocationBased } from "../../actions/LocationBasedAction";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  Backdrop,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  TextField,
  Chip,
} from "@mui/material";
import { format } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Cell,
  Tooltip as RechartsTooltip,
} from "recharts";
import { styled } from "@mui/material/styles";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TranslateIcon from "@mui/icons-material/Translate";
import LanguageIcon from "@mui/icons-material/Language";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// ------------ Translation Texts ------------
const translations = {
  en: {
    title: "Roster Attendance",
    datePicker: "Select Date",
    exportPdf: "Export Full PDF",
    exporting: "Exporting...",
    totalStrength: "Total Strength",
    todayStrength: "Today Strength(Roster)",
    todayAttendance: "Today Attendance",
    present: "Present",
    absent: "Absent",
    location: "Location",
    strength: "Strength",
    attendanceRate: "Attendance Rate (%)",
    chartTitle: "Roster Attendance by Location",
    locationsWithAttendance: "locations with attendance",
    highAttendance: "High Attendance",
    mediumAttendance: "Medium",
    lowAttendance: "Low",
    loading: "Loading roster data...",
    noData: "No roster attendance data available for the selected date.",
    total: "Total",
    reportTitle: "Roster Attendance Report",
    reportDate: "Report Date",
    summaryStats: "Summary Statistics",
    chartViz: "Roster Attendance Visualization",
    detailedData: "Detailed Roster Attendance Data",
    page: "Page",
    of: "of",
    generatedOn: "Generated on",
    pdfSuccess: "PDF exported successfully!",
    pdfError: "Failed to export PDF. Please try again.",
    prepareReport: "Preparing your report with chart and table data...",
    language: "Language",
    roster: "Roster",
    common: "Non-Roster",
    notScheduled: "Shift Mismatch",
    scheduled: "Roster Assigned",
    rosterType: "Roster Type",
    count: "Count",
  },
  si: {
    title: "රෝස්ටර් පැමිණීම්",
    datePicker: "දිනය තෝරන්න",
    exportPdf: "PDF බාගත කරන්න",
    exporting: "PDF බාගත කරමින්...",
    totalStrength: "මුළු සේවක පිරිස",
    todayStrength: "අද සේවක පිරිස",
    todayAttendance: "අද පැමිණීම්",
    present: "පැමිණි සේවකයන්",
    absent: "පැමිණ නොමැති සේවකයන්",
    location: "ස්ථානය",
    strength: "සේවක පිරිස",
    attendanceRate: "පැමිණීම් අනුපාතය (%)",
    chartTitle: "ස්ථානය අනුව රෝස්ටර් පැමිණීම්",
    locationsWithAttendance: "පැමිණීම් සහිත ස්ථාන",
    highAttendance: "ඉහළ පැමිණීම් අනුපාතය",
    mediumAttendance: "මධ්‍යම පැමිණීම් අනුපාතය",
    lowAttendance: "අඩු පැමිණීම් අනුපාතය",
    loading: "රෝස්ටර් දත්ත ලබා ගනිමින්...",
    noData: "තෝරාගත් දිනය සඳහා රෝස්ටර් පැමිණීම් දත්ත නොමැත.",
    total: "සියල්ල",
    reportTitle: "රෝස්ටර් පැමිණීම් වාර්තාව",
    reportDate: "වාර්තා දිනය",
    summaryStats: "සාරාංශ සංඛ්‍යාලේඛන",
    chartViz: "ස්ථානය අනුව රෝස්ටර් පැමිණීම් දෘශ්‍යකරණය",
    detailedData: "සවිස්තරාත්මක රෝස්ටර් පැමිණීම් දත්ත",
    page: "පිටුව",
    of: "යි",
    generatedOn: "සෑදූ දිනය",
    pdfSuccess: "PDF ගොනුව සාර්ථකව බාගත කරන ලදී!",
    pdfError: "PDF ගොනුව බාගත කිරීම අසාර්ථක විය. කරුණාකර නැවත උත්සාහ කරන්න.",
    prepareReport: "ප්‍රස්ථාරය සහ වගු දත්ත සමඟ ඔබේ වාර්තාව සකස් කරමින්...",
    language: "භාෂාව",
    roster: "රෝස්ටර්",
    common: "රෝස්ටර් නොවන",
    notScheduled: "මාරුව නොගැලපීම",
    scheduled: "රෝස්ටර් පවරා ඇත",
    rosterType: "රෝස්ටර් වර්ගය",
    count: "ගණන",
  }
};

// ------------ Styles ------------
const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: "1000px",
  margin: "0 auto",
  padding: "8px 12px",
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("sm")]: {
    padding: "0px 8px",
  },
}));

const StickyCardsContainer = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  zIndex: 100,
  paddingTop: "0px",
  paddingBottom: "4px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
  [theme.breakpoints.down("sm")]: {
    position: "sticky",
    top: 0,
    backgroundColor: "#ffffff",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "10px",
  boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
  border: "1px solid #d1d9e6",
  backgroundColor: "#ffffff",
  overflow: "hidden",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "2px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#555",
  },
  [theme.breakpoints.up("sm")]: {
    maxHeight: "380px",
    overflowY: "auto",
    overflowX: "auto",
  },
  [theme.breakpoints.down("sm")]: {
    maxHeight: "none",
    overflowY: "visible",
    overflowX: "auto",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "8px 6px",
  fontSize: "13px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.3px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    "&:first-of-type": {
      borderTopLeftRadius: "10px",
    },
    "&:last-of-type": {
      borderTopRightRadius: "10px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "6px 4px",
    fontSize: "11px",
    "&.MuiTableCell-head": {
      fontSize: "12px",
    },
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.15s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.06)",
    cursor: "pointer",
  },
  "&:last-of-type td:first-of-type": {
    borderBottomLeftRadius: "10px",
  },
  "&:last-of-type td:last-of-type": {
    borderBottomRightRadius: "10px",
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: "#f9fafc",
  borderRadius: "10px",
  padding: theme.spacing(1.5),
  boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
  border: "1px solid #e6e9ef",
  marginBottom: theme.spacing(1.5),
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  color: "#0e3c66",
  marginBottom: theme.spacing(1),
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  width: "100%",
}));

const SliderButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "#154c79",
  color: "#ffffff",
  "&:hover": {
    backgroundColor: "#0e3c66",
  },
  "&.Mui-disabled": {
    backgroundColor: "#cccccc",
    color: "#666666",
  },
  width: "32px",
  height: "32px",
}));

const CustomTooltip = ({ active, payload, language }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const t = translations[language];
    
    let rosterTypeDisplay = "";
    let rosterTypeColor = "#000000";
    
    if (data.rosterType === "N") {
      rosterTypeDisplay = t.common;
      rosterTypeColor = "#ed6c02";
    } else if (data.rosterType === "A - Scheduled") {
      rosterTypeDisplay = t.scheduled;
      rosterTypeColor = "#1976d2";
    } else if (data.rosterType === "A - Not Scheduled") {
      rosterTypeDisplay = t.notScheduled;
      rosterTypeColor = "#9c27b0";
    }

    return (
      <Paper
        sx={{
          padding: "8px 10px",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          border: "1px solid #154c79",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          minWidth: "150px",
        }}
      >
        <Box sx={{ mb: 0.5 }}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 800,
              color: "#154c79",
              textTransform: "uppercase",
            }}
          >
            {data.location}
          </Typography>
          <Typography
            sx={{
              fontSize: "9px",
              fontWeight: 600,
              color: rosterTypeColor,
              mt: 0.2,
            }}
          >
            {rosterTypeDisplay}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
              {t.strength}:
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0e3c66" }}>
              {data.total}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
              {t.present}:
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32" }}>
              {data.real}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
              {t.absent}:
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#F44336" }}>
              {data.absent}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
              {t.attendanceRate}:
            </Typography>
            <Typography 
              sx={{ 
                fontSize: "11px", 
                fontWeight: 700, 
                color: data.percentage > 70 ? "#2E7D32" : data.percentage > 50 ? "#FF9800" : "#F44336"
              }}
            >
              {data.percentage}%
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }
  return null;
};

const RosterAttendance = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { loading, data: rosterData } = useSelector((state) => state.rosterAttendance);
  const { data: locationData } = useSelector((state) => state.locationBased);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [language, setLanguage] = useState('en');
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeChartIndex, setActiveChartIndex] = useState(0);
  
  const chartContainerRefs = useRef([]);
  const t = translations[language];

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  useEffect(() => {
    dispatch(fetchRosterAttendance(formatDate(selectedDate)));
    dispatch(fetchLocationBased(formatDate(selectedDate)));
  }, [dispatch, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    const formatted = formatDate(date);
    dispatch(fetchRosterAttendance(formatted));
    dispatch(fetchLocationBased(formatted));
    setActiveChartIndex(0);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLanguageMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    handleLanguageMenuClose();
  };

  const handlePrevChart = () => {
    setActiveChartIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextChart = () => {
    setActiveChartIndex((prev) => Math.min(chartCategories.length - 1, prev + 1));
  };

  // Calculate today strength correctly: (Common TotalEmp) + (Scheduled TotalEmp) - INCLUDING THOSE WITH 0 ATTENDANCE
  const todayStrength = useMemo(() => {
    if (!rosterData || !Array.isArray(rosterData)) return 0;
    
    return rosterData
      .filter((item) => 
        item.location !== "TOTAL" && 
        (item.Roster === "N" || item.Roster === "A - Scheduled")
      )
      .reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
  }, [rosterData]);

  // Get counts for today strength locations
  const todayStrengthLocations = useMemo(() => {
    if (!rosterData || !Array.isArray(rosterData)) return { scheduled: 0, common: 0 };
    
    const scheduled = rosterData.filter(
      item => item.location !== "TOTAL" && item.Roster === "A - Scheduled"
    ).length;
    
    const common = rosterData.filter(
      item => item.location !== "TOTAL" && item.Roster === "N"
    ).length;
    
    return { scheduled, common };
  }, [rosterData]);

  // Process data for charts - Filter out locations with 0% attendance
  const processedData = useMemo(() => {
    if (!rosterData || !Array.isArray(rosterData)) return [];

    return rosterData
      .filter((item) => 
        item.location !== "TOTAL" && 
        Number(item.Percentage || 0) > 0
      )
      .map((item) => ({
        location: item.location,
        totalEmp: Number(item.TotalEmp || 0),
        todayAtt: Number(item.TodayAtt || 0),
        absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
        percentage: Math.round(Number(item.Percentage || 0)),
        rosterType: item.Roster || "N",
      }));
  }, [rosterData]);

  // Categorize data by roster type
  const categorizedData = useMemo(() => {
    const scheduled = processedData.filter(item => item.rosterType === "A - Scheduled");
    const notScheduled = processedData.filter(item => item.rosterType === "A - Not Scheduled");
    const common = processedData.filter(item => item.rosterType === "N");
    
    return {
      scheduled,
      notScheduled,
      common
    };
  }, [processedData]);

  // Chart categories for slider
  const chartCategories = useMemo(() => {
    const categories = [
      {
        title: `${t.common} (${categorizedData.common.length})`,
        data: categorizedData.common,
        type: 'common',
        color: '#ed6c02'
      },
      {
        title: `${t.scheduled} (${categorizedData.scheduled.length})`,
        data: categorizedData.scheduled,
        type: 'scheduled',
        color: '#1976d2'
      },
      {
        title: `${t.notScheduled} (${categorizedData.notScheduled.length})`,
        data: categorizedData.notScheduled,
        type: 'notScheduled',
        color: '#9c27b0'
      }
    ].filter(category => category.data.length > 0);
    
    return categories;
  }, [categorizedData, t]);

  // Chart data for active category
  const chartData = useMemo(() => {
    if (chartCategories.length === 0 || !chartCategories[activeChartIndex]) {
      return [];
    }
    
    return chartCategories[activeChartIndex].data.map((item) => ({
      location: item.location,
      total: item.totalEmp,
      real: item.todayAtt,
      absent: item.absent,
      percentage: item.percentage,
      rosterType: item.rosterType,
    }));
  }, [chartCategories, activeChartIndex]);

  // Calculate total stats
  const totalStats = useMemo(() => {
    if (!rosterData || !Array.isArray(rosterData)) {
      return {
        total: 0,
        present: 0,
        absent: 0,
        avgPercentage: 0,
        scheduledTotal: 0,
        scheduledPresent: 0,
        scheduledPercentage: 0,
        notScheduledTotal: 0,
        notScheduledPresent: 0,
        notScheduledPercentage: 0,
        commonTotal: 0,
        commonPresent: 0,
        commonPercentage: 0,
        scheduledCount: 0,
        notScheduledCount: 0,
        commonCount: 0
      };
    }

    const allData = rosterData
      .filter((item) => item.location !== "TOTAL")
      .map((item) => ({
        totalEmp: Number(item.TotalEmp || 0),
        todayAtt: Number(item.TodayAtt || 0),
        rosterType: item.Roster || "N",
      }));
    
    const total = allData.reduce((sum, item) => sum + item.totalEmp, 0);
    const present = allData.reduce((sum, item) => sum + item.todayAtt, 0);
    const absent = total - present;
    const avgPercentage = total > 0 ? Math.round((present / total) * 100) : 0;

    const scheduledItems = allData.filter(item => item.rosterType === "A - Scheduled");
    const notScheduledItems = allData.filter(item => item.rosterType === "A - Not Scheduled");
    const commonItems = allData.filter(item => item.rosterType === "N");
    
    const scheduledTotal = scheduledItems.reduce((sum, item) => sum + item.totalEmp, 0);
    const scheduledPresent = scheduledItems.reduce((sum, item) => sum + item.todayAtt, 0);
    const scheduledPercentage = scheduledTotal > 0 ? Math.round((scheduledPresent / scheduledTotal) * 100) : 0;
    
    const notScheduledTotal = notScheduledItems.reduce((sum, item) => sum + item.totalEmp, 0);
    const notScheduledPresent = notScheduledItems.reduce((sum, item) => sum + item.todayAtt, 0);
    const notScheduledPercentage = notScheduledTotal > 0 ? Math.round((notScheduledPresent / notScheduledTotal) * 100) : 0;
    
    const commonTotal = commonItems.reduce((sum, item) => sum + item.totalEmp, 0);
    const commonPresent = commonItems.reduce((sum, item) => sum + item.todayAtt, 0);
    const commonPercentage = commonTotal > 0 ? Math.round((commonPresent / commonTotal) * 100) : 0;

    return { 
      total, 
      present, 
      absent, 
      avgPercentage,
      scheduledTotal,
      scheduledPresent,
      scheduledPercentage,
      notScheduledTotal,
      notScheduledPresent,
      notScheduledPercentage,
      commonTotal,
      commonPresent,
      commonPercentage,
      scheduledCount: scheduledItems.length,
      notScheduledCount: notScheduledItems.length,
      commonCount: commonItems.length
    };
  }, [rosterData]);

  const overallTotalStrength = useMemo(() => {
    if (!locationData || !Array.isArray(locationData)) return 0;
    return locationData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
  }, [locationData]);

  // ================= PDF EXPORT FUNCTION =================
  const handleExportPDF = async () => {
    if (exporting) return;
    
    try {
      setExporting(true);
      setExportProgress(10);
      
      // Create PDF document
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Page 1: Cover page with title and summary
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text(t.reportTitle, pageWidth / 2, 40, { align: "center" });
      
      doc.setFontSize(14);
      doc.setFont("helvetica", "normal");
      doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, pageWidth / 2, 60, { align: "center" });
      
      // Summary statistics
      doc.setFontSize(12);
      doc.text(t.summaryStats, pageWidth / 2, 80, { align: "center" });
      
      const summaryY = 90;
      doc.text(`${t.totalStrength}: ${overallTotalStrength}`, 30, summaryY);
      doc.text(`${t.todayStrength}: ${todayStrength}`, 30, summaryY + 8);
      doc.text(`${t.todayAttendance}: ${totalStats.present} (${totalStats.avgPercentage}%)`, 30, summaryY + 16);
      doc.text(`${t.absent}: ${totalStats.absent} (${100 - totalStats.avgPercentage}%)`, 30, summaryY + 24);
      
      // Type breakdown
      doc.text(`Roster Assigned: ${totalStats.scheduledCount} locations, ${totalStats.scheduledPresent}/${totalStats.scheduledTotal} (${totalStats.scheduledPercentage}%)`, 30, summaryY + 35);
      doc.text(`Shift Mismatch: ${totalStats.notScheduledCount} locations, ${totalStats.notScheduledPresent}/${totalStats.notScheduledTotal} (${totalStats.notScheduledPercentage}%)`, 30, summaryY + 43);
      doc.text(`Non-Roster: ${totalStats.commonCount} locations, ${totalStats.commonPresent}/${totalStats.commonTotal} (${totalStats.commonPercentage}%)`, 30, summaryY + 51);
      
      doc.setDrawColor(21, 76, 121);
      doc.setLineWidth(0.5);
      doc.line(30, summaryY + 60, pageWidth - 30, summaryY + 60);
      
      setExportProgress(30);
      
      // Capture and add each chart to PDF
      for (let i = 0; i < chartCategories.length; i++) {
        const container = chartContainerRefs.current[i];
        const category = chartCategories[i];
        
        if (!container || !category) continue;
        
        // Make sure the chart is visible for capture
        const originalDisplay = container.style.display;
        container.style.display = 'block';
        
        // Wait for rendering
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Capture chart as image
        const chartCanvas = await html2canvas(container, {
          scale: 2,
          backgroundColor: "#F9FAFC",
          logging: false,
          useCORS: true,
          allowTaint: false,
        });
        
        const chartImage = chartCanvas.toDataURL("image/png");
        
        // Add new page for each chart
        if (i > 0 || i === 0) {
          doc.addPage();
        }
        
        // Add chart title
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text(`${category.title} - ${t.chartViz}`, 20, 20);
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
        
        // Add chart image
        const chartWidth = pageWidth - 40;
        const chartHeight = (chartCanvas.height * chartWidth) / chartCanvas.width;
        let chartY = 45;
        
        if (chartHeight > pageHeight - chartY - 20) {
          const maxHeight = pageHeight - chartY - 20;
          const scaledWidth = (chartCanvas.width * maxHeight) / chartCanvas.height;
          const chartX = (pageWidth - scaledWidth) / 2;
          doc.addImage(chartImage, "PNG", chartX, chartY, scaledWidth, maxHeight);
        } else {
          const chartX = (pageWidth - chartWidth) / 2;
          doc.addImage(chartImage, "PNG", chartX, chartY, chartWidth, chartHeight);
        }
        
        // Restore original display
        if (i !== activeChartIndex) {
          container.style.display = originalDisplay;
        }
        
        setExportProgress(30 + Math.floor((i + 1) / chartCategories.length * 40));
      }
      
      setExportProgress(70);
      
      // Final page: Detailed Table
      doc.addPage();
      
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(t.detailedData, 20, 20);
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
      
      // Prepare table data
      const allDataForTable = rosterData
        .filter((item) => item.location !== "TOTAL")
        .map((item) => ({
          location: item.location,
          totalEmp: Number(item.TotalEmp || 0),
          todayAtt: Number(item.TodayAtt || 0),
          absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
          percentage: Math.round(Number(item.Percentage || 0)),
          rosterType: item.Roster || "N",
        }));
      
      const tableData = allDataForTable.map((item) => {
        let typeDisplay = "";
        if (item.rosterType === "N") typeDisplay = t.common;
        else if (item.rosterType === "A - Scheduled") typeDisplay = t.scheduled;
        else if (item.rosterType === "A - Not Scheduled") typeDisplay = t.notScheduled;
        
        return [
          item.location,
          typeDisplay,
          item.totalEmp.toString(),
          item.todayAtt.toString(),
          item.absent.toString(),
          `${item.percentage}%`,
        ];
      });
      
      tableData.push([
        t.total,
        `${totalStats.scheduledCount}RA/${totalStats.notScheduledCount}SM/${totalStats.commonCount}NR`,
        totalStats.total.toString(),
        totalStats.present.toString(),
        totalStats.absent.toString(),
        `${totalStats.avgPercentage}%`,
      ]);
      
      setExportProgress(85);
      
      autoTable(doc, {
        startY: 35,
        head: [[t.location, t.rosterType, t.strength, t.todayAttendance, t.absent, t.attendanceRate]],
        body: tableData,
        styles: {
          fontSize: 9,
          cellPadding: 4,
        },
        headStyles: {
          fillColor: [21, 76, 121],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 248, 252],
        },
      });
      
      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `${t.page} ${i} ${t.of} ${pageCount}`,
          pageWidth - 20,
          pageHeight - 10,
          { align: "right" }
        );
        doc.text(
          `${t.generatedOn} ${new Date().toLocaleDateString()}`,
          20,
          pageHeight - 10
        );
      }

      setExportProgress(100);
      
      // Save the PDF
      const filename = `Roster_Attendance_Report_${formatDate(selectedDate)}.pdf`;
      doc.save(filename);
      
      showSnackbar(t.pdfSuccess, "success");
      
      setTimeout(() => {
        setExporting(false);
        setExportProgress(0);
      }, 1000);
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      showSnackbar(t.pdfError, "error");
      setExporting(false);
      setExportProgress(0);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        {/* Export Progress Backdrop */}
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
          open={exporting}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              p: 4,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              maxWidth: 400,
              width: "100%",
            }}
          >
            <CircularProgress
              size={60}
              thickness={4}
              sx={{ color: "#154c79" }}
            />
            <Typography variant="h6" color="primary" fontWeight="bold">
              {exporting ? t.exporting : t.exportPdf}
            </Typography>
            <Box sx={{ width: "100%", mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={exportProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#2e7d32",
                  },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={1}
              >
                {exportProgress}% Complete
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              {t.prepareReport}
            </Typography>
          </Box>
        </Backdrop>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Header Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#0e3c66",
              textAlign: "center",
              flex: 1,
            }}
          >
            {t.title}
          </Typography>
          
          {/* Language Selector */}
          <Tooltip title={t.language}>
            <IconButton
              onClick={handleLanguageMenuOpen}
              sx={{
                backgroundColor: language === 'si' ? '#142c54' : '#f0f0f0',
                color: language === 'si' ? 'white' : '#142c54',
                '&:hover': {
                  backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
                },
                width: '36px',
                height: '36px',
                ml: 1,
              }}
            >
              {language === 'si' ? (
                <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
              ) : (
                <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
              )}
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 120,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }
            }}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
              sx={{
                backgroundColor: language === 'en' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(20, 44, 84, 0.12)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon sx={{ fontSize: 18, color: '#142c54' }} />
                <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
                  English
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('si')}
              selected={language === 'si'}
              sx={{
                backgroundColor: language === 'si' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(20, 44, 84, 0.12)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TranslateIcon sx={{ fontSize: 18, color: '#142c54' }} />
                <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
                  සිංහල
                </Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Box>

        {/* Date Picker and Export Button */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 1.5,
            alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<CalendarTodayIcon />}
            onClick={() => setOpenDatePicker(true)}
            disabled={exporting}
            sx={{
              flex: isMobile ? 1 : 3,
              minWidth: isMobile ? "calc(50% - 4px)" : "auto",
              height: "38px",
              borderRadius: "8px",
              backgroundColor: exporting ? "#9e9e9e" : "#142c54",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "13px",
              "&:hover": { 
                backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
              },
            }}
          >
            {formatDate(selectedDate)}
          </Button>

          {!loading && processedData.length > 0 && (
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportPDF}
              disabled={exporting || chartData.length === 0}
              sx={{
                flex: 1,
                minWidth: isMobile ? "calc(50% - 4px)" : "auto",
                height: "38px",
                borderRadius: "8px",
                backgroundColor: exporting 
                  ? "#9e9e9e" 
                  : (chartData.length === 0 ? "#bdbdbd" : "#2e7d32"),
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "13px",
                "&:hover": { 
                  backgroundColor: exporting 
                    ? "#9e9e9e" 
                    : (chartData.length === 0 ? "#bdbdbd" : "#1b5e20") 
                },
              }}
            >
              {exporting ? t.exporting : t.exportPdf}
            </Button>
          )}
        </Box>

        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onChange={handleDateChange}
            onClose={() => setOpenDatePicker(false)}
            renderInput={(params) => (
              <TextField {...params} sx={{ display: "none" }} />
            )}
          />
        )}

        {/* Sticky Cards Section */}
        {!loading && processedData.length > 0 && (
          <StickyCardsContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "6px",
                mb: 1,
              }}
            >
              {/* Total Strength Card */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
                  border: "1px solid #aec4e0",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#0E3C66",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
                  {t.totalStrength}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {overallTotalStrength}
                </Typography>
              </Box>

              {/* Today Strength Card - CORRECTED */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
                  border: "1px solid #ffb74d",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#e65100",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
                  {t.todayStrength}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {todayStrength}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500 }}>
                  {todayStrengthLocations.scheduled + todayStrengthLocations.common} Locations (RA:{todayStrengthLocations.scheduled}, NR:{todayStrengthLocations.common})
                </Typography>
              </Box>

              {/* Today Attendance Card */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
                  border: "1px solid #80cbc4",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#004d40",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
                  {t.todayAttendance}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {totalStats.present}
                </Typography>
                <Typography
                  sx={{
                    fontSize: isMobile ? "9px" : "10px",
                    fontWeight: 700,
                    color:
                      totalStats.avgPercentage > 70
                        ? "#2E7D32"
                        : totalStats.avgPercentage > 50
                        ? "#FF9800"
                        : "#D32F2F",
                  }}
                >
                  ({totalStats.avgPercentage}%)
                </Typography>
              </Box>
            </Box>

            {/* Type Summary */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                mb: 1,
                p: 1,
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={`${t.common}: ${totalStats.commonCount}`}
                size="small"
                sx={{ backgroundColor: "#00634a", color: "white", fontWeight: 600 }}
              />
              <Chip
                label={`${t.scheduled}: ${totalStats.scheduledCount}`}
                size="small"
                sx={{ backgroundColor: "#1976d2", color: "white", fontWeight: 600 }}
              />
              <Chip
                label={`${t.notScheduled}: ${totalStats.notScheduledCount}`}
                size="small"
                sx={{ backgroundColor: "#da370e", color: "white", fontWeight: 600 }}
              />
            </Box>
          </StickyCardsContainer>
        )}

        {/* Content Container */}
        <Box
          sx={{
            [theme.breakpoints.down("sm")]: {
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
            },
          }}
        >
          {/* Chart Section with Slider */}
          {!loading && chartCategories.length > 0 && chartData.length > 0 && (
            <>
              {chartCategories.map((category, index) => (
                <ChartContainer 
                  key={index} 
                  ref={el => chartContainerRefs.current[index] = el}
                  sx={{ 
                    display: index === activeChartIndex ? 'block' : 'none',
                  }}
                >
                  <SliderContainer>
                    <SliderButton 
                      onClick={handlePrevChart} 
                      disabled={activeChartIndex === 0}
                      size="small"
                    >
                      <ArrowBackIosIcon fontSize="small" />
                    </SliderButton>
                    
                    <ChartTitle>
                      {category.title}
                    </ChartTitle>
                    
                    <SliderButton 
                      onClick={handleNextChart} 
                      disabled={activeChartIndex === chartCategories.length - 1}
                      size="small"
                    >
                      <ArrowForwardIosIcon fontSize="small" />
                    </SliderButton>
                  </SliderContainer>

                  <Box sx={{ width: "100%", height: isMobile ? 450 : 420 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        layout="vertical"
                        barSize={isMobile ? 24 : 28}
                        margin={{
                          top: 5,
                          right: isMobile ? 50 : 30,
                          left: 0,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid
                          strokeDasharray="2 2"
                          stroke="#e0e0e0"
                          horizontal={true}
                          vertical={false}
                        />
                        
                        <YAxis
                          dataKey="location"
                          type="category"
                          axisLine={false}
                          tickLine={false}
                          tick={{
                            fontSize: isMobile ? 10 : 11,
                            fontWeight: 600,
                            fill: "#333",
                            textAnchor: "start",
                            dx: -150,
                          }}
                          width={isMobile ? 170 : 180}
                          interval={0}
                          tickFormatter={(value) => value}
                        />

                        <XAxis
                          type="number"
                          tick={{ fontSize: isMobile ? 9 : 10 }}
                          axisLine={false}
                          tickLine={false}
                          domain={[0, 100]}
                          ticks={[0, 20, 40, 60, 80, 100]}
                          tickFormatter={(value) => `${value}%`}
                        />

                        <RechartsTooltip
                          content={<CustomTooltip language={language} />}
                          cursor={{ fill: "rgba(0,0,0,0.03)" }}
                        />

                        <Bar
                          dataKey="percentage"
                          name="Attendance Percentage"
                          radius={[0, 10, 10, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`attendance-cell-${index}`}
                              fill={
                                entry.percentage > 70
                                  ? "#4CAF50"
                                  : entry.percentage > 50
                                  ? "#FF9800"
                                  : "#F44336"
                              }
                            />
                          ))}

                          <LabelList
                            dataKey="real"
                            position="insideLeft"
                            style={{
                              fontSize: isMobile ? 9 : 10,
                              fontWeight: 700,
                              fill: "#fff",
                            }}
                            offset={12}
                          />

                          <LabelList
                            dataKey="percentage"
                            position="right"
                            formatter={(value) => `${value}%`}
                            style={{
                              fontSize: isMobile ? 10 : 11,
                              fontWeight: 600,
                              fill: "#555",
                            }}
                            offset={8}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Color Legend */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "15px",
                      mt: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <Box
                        sx={{
                          width: "14px",
                          height: "14px",
                          backgroundColor: "#4CAF50",
                          borderRadius: "3px",
                        }}
                      />
                      <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                        {t.highAttendance} ({'>'}70%)
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <Box
                        sx={{
                          width: "14px",
                          height: "14px",
                          backgroundColor: "#FF9800",
                          borderRadius: "3px",
                        }}
                      />
                      <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                        {t.mediumAttendance} (50-70%)
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <Box
                        sx={{
                          width: "14px",
                          height: "14px",
                          backgroundColor: "#F44336",
                          borderRadius: "3px",
                        }}
                      />
                      <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                        {t.lowAttendance} ({'<'}50%)
                      </Typography>
                    </Box>
                  </Box>
                </ChartContainer>
              ))}
            </>
          )}

          {/* TABLE SECTION */}
          {!loading && rosterData && rosterData.filter(item => item.location !== "TOTAL").length > 0 && (
            <StyledTableContainer component={Paper}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>{t.location}</StyledTableCell>
                    <StyledTableCell>{t.rosterType}</StyledTableCell>
                    <StyledTableCell>{t.strength}</StyledTableCell>
                    <StyledTableCell>{t.todayAttendance}</StyledTableCell>
                    <StyledTableCell>{t.absent}</StyledTableCell>
                    <StyledTableCell>{t.attendanceRate}</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rosterData
                    .filter((item) => item.location !== "TOTAL")
                    .map((row, index) => {
                      const totalEmp = Number(row.TotalEmp || 0);
                      const todayAtt = Number(row.TodayAtt || 0);
                      const percentage = Math.round(Number(row.Percentage || 0));
                      const rosterType = row.Roster || "N";
                      
                      let typeDisplay = "";
                      let typeColor = "";
                      if (rosterType === "N") {
                        typeDisplay = t.common;
                        typeColor = "#00634a";
                      } else if (rosterType === "A - Scheduled") {
                        typeDisplay = t.scheduled;
                        typeColor = "#1976d2";
                      } else if (rosterType === "A - Not Scheduled") {
                        typeDisplay = t.notScheduled;
                        typeColor = "#f00726";
                      }
                      
                      return (
                        <StyledTableRow key={index}>
                          <StyledTableCell
                            sx={{
                              textAlign: "left",
                              pl: isMobile ? 2 : 3,
                              maxWidth: "200px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: typeColor,
                              fontWeight: 600,
                            }}
                          >
                            {row.location}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              label={typeDisplay}
                              size="small"
                              sx={{
                                backgroundColor: `${typeColor}20`,
                                color: typeColor,
                                fontWeight: 600,
                                fontSize: "11px",
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell>{totalEmp}</StyledTableCell>
                          <StyledTableCell sx={{ color: "#2E7D32", fontWeight: 600 }}>
                            {todayAtt}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: "#D32F2F", fontWeight: 600 }}>
                            {totalEmp - todayAtt}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <span>{percentage}%</span>
                              <Box
                                sx={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  backgroundColor:
                                    percentage > 70
                                      ? "#4CAF50"
                                      : percentage > 50
                                      ? "#FF9800"
                                      : "#F44336",
                                }}
                              />
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  <StyledTableRow
                    sx={{
                      backgroundColor: "#154c79 !important",
                      "&:hover": { backgroundColor: "#154c79 !important" },
                    }}
                  >
                    <StyledTableCell
                      sx={{
                        color: "#ffffff",
                        fontWeight: 700,
                        textAlign: "left",
                        pl: isMobile ? 2 : 3,
                      }}
                    >
                      {t.total}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                    >
                      {totalStats.scheduledCount}RA/{totalStats.notScheduledCount}SM/{totalStats.commonCount}NR
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                    >
                      {totalStats.total}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                    >
                      {totalStats.present}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                    >
                      {totalStats.absent}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <span>{totalStats.avgPercentage}%</span>
                        <Box
                          sx={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            backgroundColor:
                              totalStats.avgPercentage > 70
                                ? "#4CAF50"
                                : totalStats.avgPercentage > 50
                                ? "#FF9800"
                                : "#F44336",
                          }}
                        />
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* No Data State */}
          {!loading && (!rosterData || rosterData.filter(item => item.location !== "TOTAL").length === 0) && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">{t.noData}</Typography>
            </Box>
          )}
        </Box>
      </PageContainer>
    </LocalizationProvider>
  );
};

export default RosterAttendance;









// import React, { useEffect, useState, useMemo, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchRosterAttendance } from "../../actions/RosterAttendanceAction";
// import { fetchLocationBased } from "../../actions/LocationBasedAction";
// import {
//   Box,
//   CircularProgress,
//   Typography,
//   Paper,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   useTheme,
//   useMediaQuery,
//   Button,
//   Snackbar,
//   Alert,
//   LinearProgress,
//   Backdrop,
//   IconButton,
//   Menu,
//   MenuItem,
//   Tooltip,
//   TextField,
//   Chip,
// } from "@mui/material";
// import { format } from "date-fns";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   ResponsiveContainer,
//   LabelList,
//   Cell,
//   Tooltip as RechartsTooltip,
// } from "recharts";
// import { styled } from "@mui/material/styles";
// import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import TranslateIcon from "@mui/icons-material/Translate";
// import LanguageIcon from "@mui/icons-material/Language";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import html2canvas from "html2canvas";
// import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
// import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// // ------------ Translation Texts ------------
// const translations = {
//   en: {
//     title: "Roster Attendance",
//     datePicker: "Select Date",
//     exportPdf: "Export Full PDF",
//     exporting: "Exporting...",
//     totalStrength: "Total Strength",
//     todayStrength: "Today Strength(Roster)",
//     todayAttendance: "Today Attendance",
//     present: "Present",
//     absent: "Absent",
//     location: "Location",
//     strength: "Strength",
//     attendanceRate: "Attendance Rate (%)",
//     chartTitle: "Roster Attendance by Location",
//     locationsWithAttendance: "locations with attendance",
//     highAttendance: "High Attendance",
//     mediumAttendance: "Medium",
//     lowAttendance: "Low",
//     loading: "Loading roster data...",
//     noData: "No roster attendance data available for the selected date.",
//     total: "Total",
//     reportTitle: "Roster Attendance Report",
//     reportDate: "Report Date",
//     summaryStats: "Summary Statistics",
//     chartViz: "Roster Attendance Visualization",
//     detailedData: "Detailed Roster Attendance Data",
//     page: "Page",
//     of: "of",
//     generatedOn: "Generated on",
//     pdfSuccess: "PDF exported successfully!",
//     pdfError: "Failed to export PDF. Please try again.",
//     prepareReport: "Preparing your report with chart and table data...",
//     language: "Language",
//     roster: "Roster",
//     common: "Common",
//     notScheduled: "Not Scheduled",
//     scheduled: "Scheduled",
//     rosterType: "Roster Type",
//     count: "Count",
//   },
//   si: {
//     title: "රෝස්ටර් පැමිණීම්",
//     datePicker: "දිනය තෝරන්න",
//     exportPdf: "PDF බාගත කරන්න",
//     exporting: "PDF බාගත කරමින්...",
//     totalStrength: "මුළු සේවක පිරිස",
//     todayStrength: "අද සේවක පිරිස",
//     todayAttendance: "අද පැමිණීම්",
//     present: "පැමිණි සේවකයන්",
//     absent: "පැමිණ නොමැති සේවකයන්",
//     location: "ස්ථානය",
//     strength: "සේවක පිරිස",
//     attendanceRate: "පැමිණීම් අනුපාතය (%)",
//     chartTitle: "ස්ථානය අනුව රෝස්ටර් පැමිණීම්",
//     locationsWithAttendance: "පැමිණීම් සහිත ස්ථාන",
//     highAttendance: "ඉහළ පැමිණීම් අනුපාතය",
//     mediumAttendance: "මධ්‍යම පැමිණීම් අනුපාතය",
//     lowAttendance: "අඩු පැමිණීම් අනුපාතය",
//     loading: "රෝස්ටර් දත්ත ලබා ගනිමින්...",
//     noData: "තෝරාගත් දිනය සඳහා රෝස්ටර් පැමිණීම් දත්ත නොමැත.",
//     total: "සියල්ල",
//     reportTitle: "රෝස්ටර් පැමිණීම් වාර්තාව",
//     reportDate: "වාර්තා දිනය",
//     summaryStats: "සාරාංශ සංඛ්‍යාලේඛන",
//     chartViz: "ස්ථානය අනුව රෝස්ටර් පැමිණීම් දෘශ්‍යකරණය",
//     detailedData: "සවිස්තරාත්මක රෝස්ටර් පැමිණීම් දත්ත",
//     page: "පිටුව",
//     of: "යි",
//     generatedOn: "සෑදූ දිනය",
//     pdfSuccess: "PDF ගොනුව සාර්ථකව බාගත කරන ලදී!",
//     pdfError: "PDF ගොනුව බාගත කිරීම අසාර්ථක විය. කරුණාකර නැවත උත්සාහ කරන්න.",
//     prepareReport: "ප්‍රස්ථාරය සහ වගු දත්ත සමඟ ඔබේ වාර්තාව සකස් කරමින්...",
//     language: "භාෂාව",
//     roster: "රෝස්ටර්",
//     common: "පොදු",
//     notScheduled: "නියමිත නොවන",
//     scheduled: "නියමිත",
//     rosterType: "රෝස්ටර් වර්ගය",
//     count: "ගණන",
//   }
// };

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "1000px",
//   margin: "0 auto",
//   padding: "8px 12px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     padding: "0px 8px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "10px",
//   boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
//   border: "1px solid #d1d9e6",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "5px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "2px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   [theme.breakpoints.up("sm")]: {
//     maxHeight: "380px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "none",
//     overflowY: "visible",
//     overflowX: "auto",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "8px 6px",
//   fontSize: "13px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.3px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "10px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "10px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "6px 4px",
//     fontSize: "11px",
//     "&.MuiTableCell-head": {
//       fontSize: "12px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.15s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "10px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "10px",
//   },
// }));

// const ChartContainer = styled(Box)(({ theme }) => ({
//   position: "relative",
//   backgroundColor: "#f9fafc",
//   borderRadius: "10px",
//   padding: theme.spacing(1.5),
//   boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
//   border: "1px solid #e6e9ef",
//   marginBottom: theme.spacing(1.5),
//   width: "100%",
//   [theme.breakpoints.down("sm")]: {
//     padding: theme.spacing(1),
//   },
// }));

// const ChartTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "14px",
//   fontWeight: 700,
//   color: "#0e3c66",
//   marginBottom: theme.spacing(1),
//   textAlign: "center",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "12px",
//   },
// }));

// const SliderContainer = styled(Box)(({ theme }) => ({
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "space-between",
//   gap: theme.spacing(1),
//   marginBottom: theme.spacing(1),
//   width: "100%",
// }));

// const SliderButton = styled(IconButton)(({ theme }) => ({
//   backgroundColor: "#154c79",
//   color: "#ffffff",
//   "&:hover": {
//     backgroundColor: "#0e3c66",
//   },
//   "&.Mui-disabled": {
//     backgroundColor: "#cccccc",
//     color: "#666666",
//   },
//   width: "32px",
//   height: "32px",
// }));

// const CustomTooltip = ({ active, payload, language }) => {
//   if (active && payload && payload.length) {
//     const data = payload[0].payload;
//     const t = translations[language];
    
//     let rosterTypeDisplay = "";
//     let rosterTypeColor = "#000000";
    
//     if (data.rosterType === "N") {
//       rosterTypeDisplay = t.common;
//       rosterTypeColor = "#ed6c02";
//     } else if (data.rosterType === "A - Scheduled") {
//       rosterTypeDisplay = t.scheduled;
//       rosterTypeColor = "#1976d2";
//     } else if (data.rosterType === "A - Not Scheduled") {
//       rosterTypeDisplay = t.notScheduled;
//       rosterTypeColor = "#9c27b0";
//     }

//     return (
//       <Paper
//         sx={{
//           padding: "8px 10px",
//           backgroundColor: "rgba(255, 255, 255, 0.98)",
//           border: "1px solid #154c79",
//           borderRadius: "6px",
//           boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
//           minWidth: "150px",
//         }}
//       >
//         <Box sx={{ mb: 0.5 }}>
//           <Typography
//             sx={{
//               fontSize: "12px",
//               fontWeight: 800,
//               color: "#154c79",
//               textTransform: "uppercase",
//             }}
//           >
//             {data.location}
//           </Typography>
//           <Typography
//             sx={{
//               fontSize: "9px",
//               fontWeight: 600,
//               color: rosterTypeColor,
//               mt: 0.2,
//             }}
//           >
//             {rosterTypeDisplay}
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.strength}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0e3c66" }}>
//               {data.total}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.present}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32" }}>
//               {data.real}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.absent}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#F44336" }}>
//               {data.absent}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.attendanceRate}:
//             </Typography>
//             <Typography 
//               sx={{ 
//                 fontSize: "11px", 
//                 fontWeight: 700, 
//                 color: data.percentage > 70 ? "#2E7D32" : data.percentage > 50 ? "#FF9800" : "#F44336"
//               }}
//             >
//               {data.percentage}%
//             </Typography>
//           </Box>
//         </Box>
//       </Paper>
//     );
//   }
//   return null;
// };

// const RosterAttendance = () => {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { loading, data: rosterData } = useSelector((state) => state.rosterAttendance);
//   const { data: locationData } = useSelector((state) => state.locationBased);
//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [exportProgress, setExportProgress] = useState(0);
//   const [exporting, setExporting] = useState(false);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });
//   const [language, setLanguage] = useState('en');
//   const [anchorEl, setAnchorEl] = useState(null);
//   const [activeChartIndex, setActiveChartIndex] = useState(0);
  
//   const chartContainerRefs = useRef([]);
//   const t = translations[language];

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     dispatch(fetchRosterAttendance(formatDate(selectedDate)));
//     dispatch(fetchLocationBased(formatDate(selectedDate)));
//   }, [dispatch, selectedDate]);

//   const handleDateChange = (date) => {
//     setSelectedDate(date);
//     setOpenDatePicker(false);
//     const formatted = formatDate(date);
//     dispatch(fetchRosterAttendance(formatted));
//     dispatch(fetchLocationBased(formatted));
//     setActiveChartIndex(0);
//   };

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbar({
//       open: true,
//       message,
//       severity,
//     });
//   };

//   const closeSnackbar = () => {
//     setSnackbar({ ...snackbar, open: false });
//   };

//   const handleLanguageMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget);
//   };

//   const handleLanguageMenuClose = () => {
//     setAnchorEl(null);
//   };

//   const handleLanguageChange = (lang) => {
//     setLanguage(lang);
//     handleLanguageMenuClose();
//   };

//   const handlePrevChart = () => {
//     setActiveChartIndex((prev) => Math.max(0, prev - 1));
//   };

//   const handleNextChart = () => {
//     setActiveChartIndex((prev) => Math.min(chartCategories.length - 1, prev + 1));
//   };

//   // Calculate today strength correctly: (Common TotalEmp) + (Scheduled TotalEmp) - INCLUDING THOSE WITH 0 ATTENDANCE
//   const todayStrength = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) return 0;
    
//     return rosterData
//       .filter((item) => 
//         item.location !== "TOTAL" && 
//         (item.Roster === "N" || item.Roster === "A - Scheduled")
//       )
//       .reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//   }, [rosterData]);

//   // Get counts for today strength locations
//   const todayStrengthLocations = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) return { scheduled: 0, common: 0 };
    
//     const scheduled = rosterData.filter(
//       item => item.location !== "TOTAL" && item.Roster === "A - Scheduled"
//     ).length;
    
//     const common = rosterData.filter(
//       item => item.location !== "TOTAL" && item.Roster === "N"
//     ).length;
    
//     return { scheduled, common };
//   }, [rosterData]);

//   // Process data for charts - Filter out locations with 0% attendance
//   const processedData = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) return [];

//     return rosterData
//       .filter((item) => 
//         item.location !== "TOTAL" && 
//         Number(item.Percentage || 0) > 0
//       )
//       .map((item) => ({
//         location: item.location,
//         totalEmp: Number(item.TotalEmp || 0),
//         todayAtt: Number(item.TodayAtt || 0),
//         absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//         percentage: Math.round(Number(item.Percentage || 0)),
//         rosterType: item.Roster || "N",
//       }));
//   }, [rosterData]);

//   // Categorize data by roster type
//   const categorizedData = useMemo(() => {
//     const scheduled = processedData.filter(item => item.rosterType === "A - Scheduled");
//     const notScheduled = processedData.filter(item => item.rosterType === "A - Not Scheduled");
//     const common = processedData.filter(item => item.rosterType === "N");
    
//     return {
//       scheduled,
//       notScheduled,
//       common
//     };
//   }, [processedData]);

//   // Chart categories for slider
//   const chartCategories = useMemo(() => {
//     const categories = [
//       {
//         title: `${t.common} (${categorizedData.common.length})`,
//         data: categorizedData.common,
//         type: 'common',
//         color: '#ed6c02'
//       },
//       {
//         title: `${t.scheduled} (${categorizedData.scheduled.length})`,
//         data: categorizedData.scheduled,
//         type: 'scheduled',
//         color: '#1976d2'
//       },
//       {
//         title: `${t.notScheduled} (${categorizedData.notScheduled.length})`,
//         data: categorizedData.notScheduled,
//         type: 'notScheduled',
//         color: '#9c27b0'
//       }
//     ].filter(category => category.data.length > 0);
    
//     return categories;
//   }, [categorizedData, t]);

//   // Chart data for active category
//   const chartData = useMemo(() => {
//     if (chartCategories.length === 0 || !chartCategories[activeChartIndex]) {
//       return [];
//     }
    
//     return chartCategories[activeChartIndex].data.map((item) => ({
//       location: item.location,
//       total: item.totalEmp,
//       real: item.todayAtt,
//       absent: item.absent,
//       percentage: item.percentage,
//       rosterType: item.rosterType,
//     }));
//   }, [chartCategories, activeChartIndex]);

//   // Calculate total stats
//   const totalStats = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) {
//       return {
//         total: 0,
//         present: 0,
//         absent: 0,
//         avgPercentage: 0,
//         scheduledTotal: 0,
//         scheduledPresent: 0,
//         scheduledPercentage: 0,
//         notScheduledTotal: 0,
//         notScheduledPresent: 0,
//         notScheduledPercentage: 0,
//         commonTotal: 0,
//         commonPresent: 0,
//         commonPercentage: 0,
//         scheduledCount: 0,
//         notScheduledCount: 0,
//         commonCount: 0
//       };
//     }

//     const allData = rosterData
//       .filter((item) => item.location !== "TOTAL")
//       .map((item) => ({
//         totalEmp: Number(item.TotalEmp || 0),
//         todayAtt: Number(item.TodayAtt || 0),
//         rosterType: item.Roster || "N",
//       }));
    
//     const total = allData.reduce((sum, item) => sum + item.totalEmp, 0);
//     const present = allData.reduce((sum, item) => sum + item.todayAtt, 0);
//     const absent = total - present;
//     const avgPercentage = total > 0 ? Math.round((present / total) * 100) : 0;

//     const scheduledItems = allData.filter(item => item.rosterType === "A - Scheduled");
//     const notScheduledItems = allData.filter(item => item.rosterType === "A - Not Scheduled");
//     const commonItems = allData.filter(item => item.rosterType === "N");
    
//     const scheduledTotal = scheduledItems.reduce((sum, item) => sum + item.totalEmp, 0);
//     const scheduledPresent = scheduledItems.reduce((sum, item) => sum + item.todayAtt, 0);
//     const scheduledPercentage = scheduledTotal > 0 ? Math.round((scheduledPresent / scheduledTotal) * 100) : 0;
    
//     const notScheduledTotal = notScheduledItems.reduce((sum, item) => sum + item.totalEmp, 0);
//     const notScheduledPresent = notScheduledItems.reduce((sum, item) => sum + item.todayAtt, 0);
//     const notScheduledPercentage = notScheduledTotal > 0 ? Math.round((notScheduledPresent / notScheduledTotal) * 100) : 0;
    
//     const commonTotal = commonItems.reduce((sum, item) => sum + item.totalEmp, 0);
//     const commonPresent = commonItems.reduce((sum, item) => sum + item.todayAtt, 0);
//     const commonPercentage = commonTotal > 0 ? Math.round((commonPresent / commonTotal) * 100) : 0;

//     return { 
//       total, 
//       present, 
//       absent, 
//       avgPercentage,
//       scheduledTotal,
//       scheduledPresent,
//       scheduledPercentage,
//       notScheduledTotal,
//       notScheduledPresent,
//       notScheduledPercentage,
//       commonTotal,
//       commonPresent,
//       commonPercentage,
//       scheduledCount: scheduledItems.length,
//       notScheduledCount: notScheduledItems.length,
//       commonCount: commonItems.length
//     };
//   }, [rosterData]);

//   const overallTotalStrength = useMemo(() => {
//     if (!locationData || !Array.isArray(locationData)) return 0;
//     return locationData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//   }, [locationData]);

//   // ================= PDF EXPORT FUNCTION =================
//   const handleExportPDF = async () => {
//     if (exporting) return;
    
//     try {
//       setExporting(true);
//       setExportProgress(10);
      
//       // Create PDF document
//       const doc = new jsPDF("p", "mm", "a4");
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();
      
//       // Page 1: Cover page with title and summary
//       doc.setFontSize(20);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.reportTitle, pageWidth / 2, 40, { align: "center" });
      
//       doc.setFontSize(14);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, pageWidth / 2, 60, { align: "center" });
      
//       // Summary statistics
//       doc.setFontSize(12);
//       doc.text(t.summaryStats, pageWidth / 2, 80, { align: "center" });
      
//       const summaryY = 90;
//       doc.text(`${t.totalStrength}: ${overallTotalStrength}`, 30, summaryY);
//       doc.text(`${t.todayStrength}: ${todayStrength}`, 30, summaryY + 8);
//       doc.text(`${t.todayAttendance}: ${totalStats.present} (${totalStats.avgPercentage}%)`, 30, summaryY + 16);
//       doc.text(`${t.absent}: ${totalStats.absent} (${100 - totalStats.avgPercentage}%)`, 30, summaryY + 24);
      
//       // Type breakdown
//       doc.text(`Scheduled: ${totalStats.scheduledCount} locations, ${totalStats.scheduledPresent}/${totalStats.scheduledTotal} (${totalStats.scheduledPercentage}%)`, 30, summaryY + 35);
//       doc.text(`Not Scheduled: ${totalStats.notScheduledCount} locations, ${totalStats.notScheduledPresent}/${totalStats.notScheduledTotal} (${totalStats.notScheduledPercentage}%)`, 30, summaryY + 43);
//       doc.text(`Common: ${totalStats.commonCount} locations, ${totalStats.commonPresent}/${totalStats.commonTotal} (${totalStats.commonPercentage}%)`, 30, summaryY + 51);
      
//       doc.setDrawColor(21, 76, 121);
//       doc.setLineWidth(0.5);
//       doc.line(30, summaryY + 60, pageWidth - 30, summaryY + 60);
      
//       setExportProgress(30);
      
//       // Capture and add each chart to PDF
//       for (let i = 0; i < chartCategories.length; i++) {
//         const container = chartContainerRefs.current[i];
//         const category = chartCategories[i];
        
//         if (!container || !category) continue;
        
//         // Make sure the chart is visible for capture
//         const originalDisplay = container.style.display;
//         container.style.display = 'block';
        
//         // Wait for rendering
//         await new Promise(resolve => setTimeout(resolve, 300));
        
//         // Capture chart as image
//         const chartCanvas = await html2canvas(container, {
//           scale: 2,
//           backgroundColor: "#F9FAFC",
//           logging: false,
//           useCORS: true,
//           allowTaint: false,
//         });
        
//         const chartImage = chartCanvas.toDataURL("image/png");
        
//         // Add new page for each chart
//         if (i > 0 || i === 0) {
//           doc.addPage();
//         }
        
//         // Add chart title
//         doc.setFontSize(16);
//         doc.setFont("helvetica", "bold");
//         doc.text(`${category.title} - ${t.chartViz}`, 20, 20);
        
//         doc.setFontSize(12);
//         doc.setFont("helvetica", "normal");
//         doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
        
//         // Add chart image
//         const chartWidth = pageWidth - 40;
//         const chartHeight = (chartCanvas.height * chartWidth) / chartCanvas.width;
//         let chartY = 45;
        
//         if (chartHeight > pageHeight - chartY - 20) {
//           const maxHeight = pageHeight - chartY - 20;
//           const scaledWidth = (chartCanvas.width * maxHeight) / chartCanvas.height;
//           const chartX = (pageWidth - scaledWidth) / 2;
//           doc.addImage(chartImage, "PNG", chartX, chartY, scaledWidth, maxHeight);
//         } else {
//           const chartX = (pageWidth - chartWidth) / 2;
//           doc.addImage(chartImage, "PNG", chartX, chartY, chartWidth, chartHeight);
//         }
        
//         // Restore original display
//         if (i !== activeChartIndex) {
//           container.style.display = originalDisplay;
//         }
        
//         setExportProgress(30 + Math.floor((i + 1) / chartCategories.length * 40));
//       }
      
//       setExportProgress(70);
      
//       // Final page: Detailed Table
//       doc.addPage();
      
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.detailedData, 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
      
//       // Prepare table data
//       const allDataForTable = rosterData
//         .filter((item) => item.location !== "TOTAL")
//         .map((item) => ({
//           location: item.location,
//           totalEmp: Number(item.TotalEmp || 0),
//           todayAtt: Number(item.TodayAtt || 0),
//           absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//           percentage: Math.round(Number(item.Percentage || 0)),
//           rosterType: item.Roster || "N",
//         }));
      
//       const tableData = allDataForTable.map((item) => {
//         let typeDisplay = "";
//         if (item.rosterType === "N") typeDisplay = t.common;
//         else if (item.rosterType === "A - Scheduled") typeDisplay = t.scheduled;
//         else if (item.rosterType === "A - Not Scheduled") typeDisplay = t.notScheduled;
        
//         return [
//           item.location,
//           typeDisplay,
//           item.totalEmp.toString(),
//           item.todayAtt.toString(),
//           item.absent.toString(),
//           `${item.percentage}%`,
//         ];
//       });
      
//       tableData.push([
//         t.total,
//         `${totalStats.scheduledCount}S/${totalStats.notScheduledCount}NS/${totalStats.commonCount}C`,
//         totalStats.total.toString(),
//         totalStats.present.toString(),
//         totalStats.absent.toString(),
//         `${totalStats.avgPercentage}%`,
//       ]);
      
//       setExportProgress(85);
      
//       autoTable(doc, {
//         startY: 35,
//         head: [[t.location, t.rosterType, t.strength, t.todayAttendance, t.absent, t.attendanceRate]],
//         body: tableData,
//         styles: {
//           fontSize: 9,
//           cellPadding: 4,
//         },
//         headStyles: {
//           fillColor: [21, 76, 121],
//           textColor: 255,
//           fontStyle: "bold",
//         },
//         alternateRowStyles: {
//           fillColor: [245, 248, 252],
//         },
//       });
      
//       // Footer on all pages
//       const pageCount = doc.internal.getNumberOfPages();
//       for (let i = 1; i <= pageCount; i++) {
//         doc.setPage(i);
//         doc.setFontSize(10);
//         doc.setTextColor(128, 128, 128);
//         doc.text(
//           `${t.page} ${i} ${t.of} ${pageCount}`,
//           pageWidth - 20,
//           pageHeight - 10,
//           { align: "right" }
//         );
//         doc.text(
//           `${t.generatedOn} ${new Date().toLocaleDateString()}`,
//           20,
//           pageHeight - 10
//         );
//       }

//       setExportProgress(100);
      
//       // Save the PDF
//       const filename = `Roster_Attendance_Report_${formatDate(selectedDate)}.pdf`;
//       doc.save(filename);
      
//       showSnackbar(t.pdfSuccess, "success");
      
//       setTimeout(() => {
//         setExporting(false);
//         setExportProgress(0);
//       }, 1000);
      
//     } catch (error) {
//       console.error("Error generating PDF:", error);
//       showSnackbar(t.pdfError, "error");
//       setExporting(false);
//       setExportProgress(0);
//     }
//   };

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         {/* Export Progress Backdrop */}
//         <Backdrop
//           sx={{
//             color: "#fff",
//             zIndex: (theme) => theme.zIndex.drawer + 1,
//             backgroundColor: "rgba(0, 0, 0, 0.7)",
//           }}
//           open={exporting}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//               gap: 2,
//               p: 4,
//               bgcolor: "background.paper",
//               borderRadius: 2,
//               boxShadow: 24,
//               maxWidth: 400,
//               width: "100%",
//             }}
//           >
//             <CircularProgress
//               size={60}
//               thickness={4}
//               sx={{ color: "#154c79" }}
//             />
//             <Typography variant="h6" color="primary" fontWeight="bold">
//               {exporting ? t.exporting : t.exportPdf}
//             </Typography>
//             <Box sx={{ width: "100%", mt: 2 }}>
//               <LinearProgress
//                 variant="determinate"
//                 value={exportProgress}
//                 sx={{
//                   height: 10,
//                   borderRadius: 5,
//                   backgroundColor: "#e0e0e0",
//                   "& .MuiLinearProgress-bar": {
//                     backgroundColor: "#2e7d32",
//                   },
//                 }}
//               />
//               <Typography
//                 variant="body2"
//                 color="text.secondary"
//                 align="center"
//                 mt={1}
//               >
//                 {exportProgress}% Complete
//               </Typography>
//             </Box>
//             <Typography variant="body2" color="text.secondary" align="center">
//               {t.prepareReport}
//             </Typography>
//           </Box>
//         </Backdrop>

//         {/* Success/Error Snackbar */}
//         <Snackbar
//           open={snackbar.open}
//           autoHideDuration={4000}
//           onClose={closeSnackbar}
//           anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//         >
//           <Alert
//             onClose={closeSnackbar}
//             severity={snackbar.severity}
//             sx={{ width: "100%" }}
//             elevation={6}
//             variant="filled"
//           >
//             {snackbar.message}
//           </Alert>
//         </Snackbar>

//         {/* Header Section */}
//         <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
//           <Typography
//             sx={{
//               fontSize: "16px",
//               fontWeight: 700,
//               color: "#0e3c66",
//               textAlign: "center",
//               flex: 1,
//             }}
//           >
//             {t.title}
//           </Typography>
          
//           {/* Language Selector */}
//           <Tooltip title={t.language}>
//             <IconButton
//               onClick={handleLanguageMenuOpen}
//               sx={{
//                 backgroundColor: language === 'si' ? '#142c54' : '#f0f0f0',
//                 color: language === 'si' ? 'white' : '#142c54',
//                 '&:hover': {
//                   backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
//                 },
//                 width: '36px',
//                 height: '36px',
//                 ml: 1,
//               }}
//             >
//               {language === 'si' ? (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
//               ) : (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
//               )}
//             </IconButton>
//           </Tooltip>
          
//           <Menu
//             anchorEl={anchorEl}
//             open={Boolean(anchorEl)}
//             onClose={handleLanguageMenuClose}
//             PaperProps={{
//               sx: {
//                 mt: 1,
//                 minWidth: 120,
//                 borderRadius: '8px',
//                 boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
//               }
//             }}
//           >
//             <MenuItem 
//               onClick={() => handleLanguageChange('en')}
//               selected={language === 'en'}
//               sx={{
//                 backgroundColor: language === 'en' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <LanguageIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
//                   English
//                 </Typography>
//               </Box>
//             </MenuItem>
//             <MenuItem 
//               onClick={() => handleLanguageChange('si')}
//               selected={language === 'si'}
//               sx={{
//                 backgroundColor: language === 'si' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <TranslateIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
//                   සිංහල
//                 </Typography>
//               </Box>
//             </MenuItem>
//           </Menu>
//         </Box>

//         {/* Date Picker and Export Button */}
//         <Box
//           sx={{
//             display: "flex",
//             gap: 1,
//             mb: 1.5,
//             alignItems: "center",
//             flexWrap: isMobile ? "wrap" : "nowrap",
//           }}
//         >
//           <Button
//             variant="contained"
//             startIcon={<CalendarTodayIcon />}
//             onClick={() => setOpenDatePicker(true)}
//             disabled={exporting}
//             sx={{
//               flex: isMobile ? 1 : 3,
//               minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//               height: "38px",
//               borderRadius: "8px",
//               backgroundColor: exporting ? "#9e9e9e" : "#142c54",
//               color: "#fff",
//               fontWeight: "bold",
//               textTransform: "none",
//               fontSize: "13px",
//               "&:hover": { 
//                 backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
//               },
//             }}
//           >
//             {formatDate(selectedDate)}
//           </Button>

//           {!loading && processedData.length > 0 && (
//             <Button
//               variant="contained"
//               startIcon={<PictureAsPdfIcon />}
//               onClick={handleExportPDF}
//               disabled={exporting || chartData.length === 0}
//               sx={{
//                 flex: 1,
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//                 height: "38px",
//                 borderRadius: "8px",
//                 backgroundColor: exporting 
//                   ? "#9e9e9e" 
//                   : (chartData.length === 0 ? "#bdbdbd" : "#2e7d32"),
//                 color: "#fff",
//                 fontWeight: "bold",
//                 textTransform: "none",
//                 fontSize: "13px",
//                 "&:hover": { 
//                   backgroundColor: exporting 
//                     ? "#9e9e9e" 
//                     : (chartData.length === 0 ? "#bdbdbd" : "#1b5e20") 
//                 },
//               }}
//             >
//               {exporting ? t.exporting : t.exportPdf}
//             </Button>
//           )}
//         </Box>

//         {openDatePicker && (
//           <DatePicker
//             open={openDatePicker}
//             value={selectedDate}
//             onChange={handleDateChange}
//             onClose={() => setOpenDatePicker(false)}
//             renderInput={(params) => (
//               <TextField {...params} sx={{ display: "none" }} />
//             )}
//           />
//         )}

//         {/* Sticky Cards Section */}
//         {!loading && processedData.length > 0 && (
//           <StickyCardsContainer>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: "6px",
//                 mb: 1,
//               }}
//             >
//               {/* Total Strength Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                   border: "1px solid #aec4e0",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0E3C66",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.totalStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {overallTotalStrength}
//                 </Typography>
//               </Box>

//               {/* Today Strength Card - CORRECTED */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
//                   border: "1px solid #ffb74d",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#e65100",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.todayStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {todayStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500 }}>
//                   {todayStrengthLocations.scheduled + todayStrengthLocations.common} Locations (S:{todayStrengthLocations.scheduled}, C:{todayStrengthLocations.common})
//                 </Typography>
//               </Box>

//               {/* Today Attendance Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//                   border: "1px solid #80cbc4",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#004d40",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.todayAttendance}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {totalStats.present}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color:
//                       totalStats.avgPercentage > 70
//                         ? "#2E7D32"
//                         : totalStats.avgPercentage > 50
//                         ? "#FF9800"
//                         : "#D32F2F",
//                   }}
//                 >
//                   ({totalStats.avgPercentage}%)
//                 </Typography>
//               </Box>
//             </Box>

//             {/* Type Summary */}
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: "10px",
//                 mb: 1,
//                 p: 1,
//                 backgroundColor: "#f5f5f5",
//                 borderRadius: "6px",
//                 flexWrap: "wrap",
//               }}
//             >
//               <Chip
//                 label={`${t.common}: ${totalStats.commonCount}`}
//                 size="small"
//                 sx={{ backgroundColor: "#00634a", color: "white", fontWeight: 600 }}
//               />
//               <Chip
//                 label={`${t.scheduled}: ${totalStats.scheduledCount}`}
//                 size="small"
//                 sx={{ backgroundColor: "#1976d2", color: "white", fontWeight: 600 }}
//               />
//               <Chip
//                 label={`${t.notScheduled}: ${totalStats.notScheduledCount}`}
//                 size="small"
//                 sx={{ backgroundColor: "#da370e", color: "white", fontWeight: 600 }}
//               />
//             </Box>
//           </StickyCardsContainer>
//         )}

//         {/* Content Container */}
//         <Box
//           sx={{
//             [theme.breakpoints.down("sm")]: {
//               maxHeight: "calc(100vh - 300px)",
//               overflowY: "auto",
//             },
//           }}
//         >
//           {/* Chart Section with Slider */}
//           {!loading && chartCategories.length > 0 && chartData.length > 0 && (
//             <>
//               {chartCategories.map((category, index) => (
//                 <ChartContainer 
//                   key={index} 
//                   ref={el => chartContainerRefs.current[index] = el}
//                   sx={{ 
//                     display: index === activeChartIndex ? 'block' : 'none',
//                   }}
//                 >
//                   <SliderContainer>
//                     <SliderButton 
//                       onClick={handlePrevChart} 
//                       disabled={activeChartIndex === 0}
//                       size="small"
//                     >
//                       <ArrowBackIosIcon fontSize="small" />
//                     </SliderButton>
                    
//                     <ChartTitle>
//                       {category.title}
//                     </ChartTitle>
                    
//                     <SliderButton 
//                       onClick={handleNextChart} 
//                       disabled={activeChartIndex === chartCategories.length - 1}
//                       size="small"
//                     >
//                       <ArrowForwardIosIcon fontSize="small" />
//                     </SliderButton>
//                   </SliderContainer>

//                   <Box sx={{ width: "100%", height: isMobile ? 450 : 420 }}>
//                     <ResponsiveContainer width="100%" height="100%">
//                       <BarChart
//                         data={chartData}
//                         layout="vertical"
//                         barSize={isMobile ? 24 : 28}
//                         margin={{
//                           top: 5,
//                           right: isMobile ? 50 : 30,
//                           left: 0,
//                           bottom: 5,
//                         }}
//                       >
//                         <CartesianGrid
//                           strokeDasharray="2 2"
//                           stroke="#e0e0e0"
//                           horizontal={true}
//                           vertical={false}
//                         />
                        
//                         <YAxis
//                           dataKey="location"
//                           type="category"
//                           axisLine={false}
//                           tickLine={false}
//                           tick={{
//                             fontSize: isMobile ? 10 : 11,
//                             fontWeight: 600,
//                             fill: "#333",
//                             textAnchor: "start",
//                             dx: -150,
//                           }}
//                           width={isMobile ? 170 : 180}
//                           interval={0}
//                           tickFormatter={(value) => value}
//                         />

//                         <XAxis
//                           type="number"
//                           tick={{ fontSize: isMobile ? 9 : 10 }}
//                           axisLine={false}
//                           tickLine={false}
//                           domain={[0, 100]}
//                           ticks={[0, 20, 40, 60, 80, 100]}
//                           tickFormatter={(value) => `${value}%`}
//                         />

//                         <RechartsTooltip
//                           content={<CustomTooltip language={language} />}
//                           cursor={{ fill: "rgba(0,0,0,0.03)" }}
//                         />

//                         <Bar
//                           dataKey="percentage"
//                           name="Attendance Percentage"
//                           radius={[0, 10, 10, 0]}
//                         >
//                           {chartData.map((entry, index) => (
//                             <Cell
//                               key={`attendance-cell-${index}`}
//                               fill={
//                                 entry.percentage > 70
//                                   ? "#4CAF50"
//                                   : entry.percentage > 50
//                                   ? "#FF9800"
//                                   : "#F44336"
//                               }
//                             />
//                           ))}

//                           <LabelList
//                             dataKey="real"
//                             position="insideLeft"
//                             style={{
//                               fontSize: isMobile ? 9 : 10,
//                               fontWeight: 700,
//                               fill: "#fff",
//                             }}
//                             offset={12}
//                           />

//                           <LabelList
//                             dataKey="percentage"
//                             position="right"
//                             formatter={(value) => `${value}%`}
//                             style={{
//                               fontSize: isMobile ? 10 : 11,
//                               fontWeight: 600,
//                               fill: "#555",
//                             }}
//                             offset={8}
//                           />
//                         </Bar>
//                       </BarChart>
//                     </ResponsiveContainer>
//                   </Box>

//                   {/* Color Legend */}
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "center",
//                       gap: "15px",
//                       mt: 1,
//                       flexWrap: "wrap",
//                     }}
//                   >
//                     <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                       <Box
//                         sx={{
//                           width: "14px",
//                           height: "14px",
//                           backgroundColor: "#4CAF50",
//                           borderRadius: "3px",
//                         }}
//                       />
//                       <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                         {t.highAttendance} ({'>'}70%)
//                       </Typography>
//                     </Box>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                       <Box
//                         sx={{
//                           width: "14px",
//                           height: "14px",
//                           backgroundColor: "#FF9800",
//                           borderRadius: "3px",
//                         }}
//                       />
//                       <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                         {t.mediumAttendance} (50-70%)
//                       </Typography>
//                     </Box>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                       <Box
//                         sx={{
//                           width: "14px",
//                           height: "14px",
//                           backgroundColor: "#F44336",
//                           borderRadius: "3px",
//                         }}
//                       />
//                       <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                         {t.lowAttendance} ({'<'}50%)
//                       </Typography>
//                     </Box>
//                   </Box>
//                 </ChartContainer>
//               ))}
//             </>
//           )}

//           {/* TABLE SECTION */}
//           {!loading && rosterData && rosterData.filter(item => item.location !== "TOTAL").length > 0 && (
//             <StyledTableContainer component={Paper}>
//               <Table stickyHeader>
//                 <TableHead>
//                   <TableRow>
//                     <StyledTableCell>{t.location}</StyledTableCell>
//                     <StyledTableCell>{t.rosterType}</StyledTableCell>
//                     <StyledTableCell>{t.strength}</StyledTableCell>
//                     <StyledTableCell>{t.todayAttendance}</StyledTableCell>
//                     <StyledTableCell>{t.absent}</StyledTableCell>
//                     <StyledTableCell>{t.attendanceRate}</StyledTableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {rosterData
//                     .filter((item) => item.location !== "TOTAL")
//                     .map((row, index) => {
//                       const totalEmp = Number(row.TotalEmp || 0);
//                       const todayAtt = Number(row.TodayAtt || 0);
//                       const percentage = Math.round(Number(row.Percentage || 0));
//                       const rosterType = row.Roster || "N";
                      
//                       let typeDisplay = "";
//                       let typeColor = "";
//                       if (rosterType === "N") {
//                         typeDisplay = t.common;
//                         typeColor = "#00634a";
//                       } else if (rosterType === "A - Scheduled") {
//                         typeDisplay = t.scheduled;
//                         typeColor = "#1976d2";
//                       } else if (rosterType === "A - Not Scheduled") {
//                         typeDisplay = t.notScheduled;
//                         typeColor = "#f00726";
//                       }
                      
//                       return (
//                         <StyledTableRow key={index}>
//                           <StyledTableCell
//                             sx={{
//                               textAlign: "left",
//                               pl: isMobile ? 2 : 3,
//                               maxWidth: "200px",
//                               overflow: "hidden",
//                               textOverflow: "ellipsis",
//                               whiteSpace: "nowrap",
//                               color: typeColor,
//                               fontWeight: 600,
//                             }}
//                           >
//                             {row.location}
//                           </StyledTableCell>
//                           <StyledTableCell>
//                             <Chip
//                               label={typeDisplay}
//                               size="small"
//                               sx={{
//                                 backgroundColor: `${typeColor}20`,
//                                 color: typeColor,
//                                 fontWeight: 600,
//                                 fontSize: "11px",
//                               }}
//                             />
//                           </StyledTableCell>
//                           <StyledTableCell>{totalEmp}</StyledTableCell>
//                           <StyledTableCell sx={{ color: "#2E7D32", fontWeight: 600 }}>
//                             {todayAtt}
//                           </StyledTableCell>
//                           <StyledTableCell sx={{ color: "#D32F2F", fontWeight: 600 }}>
//                             {totalEmp - todayAtt}
//                           </StyledTableCell>
//                           <StyledTableCell>
//                             <Box
//                               sx={{
//                                 display: "inline-flex",
//                                 alignItems: "center",
//                                 gap: 0.5,
//                               }}
//                             >
//                               <span>{percentage}%</span>
//                               <Box
//                                 sx={{
//                                   width: "6px",
//                                   height: "6px",
//                                   borderRadius: "50%",
//                                   backgroundColor:
//                                     percentage > 70
//                                       ? "#4CAF50"
//                                       : percentage > 50
//                                       ? "#FF9800"
//                                       : "#F44336",
//                                 }}
//                               />
//                             </Box>
//                           </StyledTableCell>
//                         </StyledTableRow>
//                       );
//                     })}
//                   <StyledTableRow
//                     sx={{
//                       backgroundColor: "#154c79 !important",
//                       "&:hover": { backgroundColor: "#154c79 !important" },
//                     }}
//                   >
//                     <StyledTableCell
//                       sx={{
//                         color: "#ffffff",
//                         fontWeight: 700,
//                         textAlign: "left",
//                         pl: isMobile ? 2 : 3,
//                       }}
//                     >
//                       {t.total}
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       {totalStats.scheduledCount}S/{totalStats.notScheduledCount}NS/{totalStats.commonCount}C
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       {totalStats.total}
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       {totalStats.present}
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       {totalStats.absent}
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       <Box
//                         sx={{
//                           display: "inline-flex",
//                           alignItems: "center",
//                           gap: 0.5,
//                         }}
//                       >
//                         <span>{totalStats.avgPercentage}%</span>
//                         <Box
//                           sx={{
//                             width: "6px",
//                             height: "6px",
//                             borderRadius: "50%",
//                             backgroundColor:
//                               totalStats.avgPercentage > 70
//                                 ? "#4CAF50"
//                                 : totalStats.avgPercentage > 50
//                                 ? "#FF9800"
//                                 : "#F44336",
//                           }}
//                         />
//                       </Box>
//                     </StyledTableCell>
//                   </StyledTableRow>
//                 </TableBody>
//               </Table>
//             </StyledTableContainer>
//           )}

//           {/* Loading State */}
//           {loading && (
//             <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
//               <CircularProgress />
//             </Box>
//           )}

//           {/* No Data State */}
//           {!loading && (!rosterData || rosterData.filter(item => item.location !== "TOTAL").length === 0) && (
//             <Box sx={{ textAlign: "center", py: 4 }}>
//               <Typography color="textSecondary">{t.noData}</Typography>
//             </Box>
//           )}
//         </Box>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// };

// export default RosterAttendance;






// import React, { useEffect, useState, useMemo, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchRosterAttendance } from "../../actions/RosterAttendanceAction";
// import { fetchLocationBased } from "../../actions/LocationBasedAction";
// import {
//   Box,
//   CircularProgress,
//   Typography,
//   Paper,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   useTheme,
//   useMediaQuery,
//   Button,
//   Snackbar,
//   Alert,
//   LinearProgress,
//   Backdrop,
//   IconButton,
//   Menu,
//   MenuItem,
//   Tooltip,
//   TextField,
//   Chip,
// } from "@mui/material";
// import { format } from "date-fns";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   ResponsiveContainer,
//   LabelList,
//   Cell,
//   Tooltip as RechartsTooltip,
// } from "recharts";
// import { styled } from "@mui/material/styles";
// import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import TranslateIcon from "@mui/icons-material/Translate";
// import LanguageIcon from "@mui/icons-material/Language";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import html2canvas from "html2canvas";
// import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
// import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// // ------------ Translation Texts ------------
// const translations = {
//   en: {
//     title: "Roster Attendance",
//     datePicker: "Select Date",
//     exportPdf: "Export Full PDF",
//     exporting: "Exporting...",
//     totalStrength: "Total Strength",
//     todayStrength: "Today Strength",
//     todayAttendance: "Today Attendance",
//     present: "Present",
//     absent: "Absent",
//     location: "Location",
//     strength: "Strength",
//     attendanceRate: "Attendance Rate (%)",
//     chartTitle: "Roster Attendance by Location",
//     locationsWithAttendance: "locations with attendance",
//     highAttendance: "High Attendance",
//     mediumAttendance: "Medium",
//     lowAttendance: "Low",
//     loading: "Loading roster data...",
//     noData: "No roster attendance data available for the selected date.",
//     total: "Total",
//     reportTitle: "Roster Attendance Report",
//     reportDate: "Report Date",
//     summaryStats: "Summary Statistics",
//     chartViz: "Roster Attendance Visualization",
//     detailedData: "Detailed Roster Attendance Data",
//     page: "Page",
//     of: "of",
//     generatedOn: "Generated on",
//     pdfSuccess: "PDF exported successfully!",
//     pdfError: "Failed to export PDF. Please try again.",
//     prepareReport: "Preparing your report with chart and table data...",
//     language: "Language",
//     roster: "Roster",
//     common: "Common",
//     notScheduled: "Not Scheduled",
//     scheduled: "Scheduled",
//     rosterType: "Roster Type",
//     count: "Count",
//   },
//   si: {
//     title: "රෝස්ටර් පැමිණීම්",
//     datePicker: "දිනය තෝරන්න",
//     exportPdf: "PDF බාගත කරන්න",
//     exporting: "PDF බාගත කරමින්...",
//     totalStrength: "මුළු සේවක පිරිස",
//     todayStrength: "අද සේවක පිරිස",
//     todayAttendance: "අද පැමිණීම්",
//     present: "පැමිණි සේවකයන්",
//     absent: "පැමිණ නොමැති සේවකයන්",
//     location: "ස්ථානය",
//     strength: "සේවක පිරිස",
//     attendanceRate: "පැමිණීම් අනුපාතය (%)",
//     chartTitle: "ස්ථානය අනුව රෝස්ටර් පැමිණීම්",
//     locationsWithAttendance: "පැමිණීම් සහිත ස්ථාන",
//     highAttendance: "ඉහළ පැමිණීම් අනුපාතය",
//     mediumAttendance: "මධ්‍යම පැමිණීම් අනුපාතය",
//     lowAttendance: "අඩු පැමිණීම් අනුපාතය",
//     loading: "රෝස්ටර් දත්ත ලබා ගනිමින්...",
//     noData: "තෝරාගත් දිනය සඳහා රෝස්ටර් පැමිණීම් දත්ත නොමැත.",
//     total: "සියල්ල",
//     reportTitle: "රෝස්ටර් පැමිණීම් වාර්තාව",
//     reportDate: "වාර්තා දිනය",
//     summaryStats: "සාරාංශ සංඛ්‍යාලේඛන",
//     chartViz: "ස්ථානය අනුව රෝස්ටර් පැමිණීම් දෘශ්‍යකරණය",
//     detailedData: "සවිස්තරාත්මක රෝස්ටර් පැමිණීම් දත්ත",
//     page: "පිටුව",
//     of: "යි",
//     generatedOn: "සෑදූ දිනය",
//     pdfSuccess: "PDF ගොනුව සාර්ථකව බාගත කරන ලදී!",
//     pdfError: "PDF ගොනුව බාගත කිරීම අසාර්ථක විය. කරුණාකර නැවත උත්සාහ කරන්න.",
//     prepareReport: "ප්‍රස්ථාරය සහ වගු දත්ත සමඟ ඔබේ වාර්තාව සකස් කරමින්...",
//     language: "භාෂාව",
//     roster: "රෝස්ටර්",
//     common: "පොදු",
//     notScheduled: "නියමිත නොවන",
//     scheduled: "නියමිත",
//     rosterType: "රෝස්ටර් වර්ගය",
//     count: "ගණන",
//   }
// };

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "1000px",
//   margin: "0 auto",
//   padding: "8px 12px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     padding: "0px 8px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "10px",
//   boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
//   border: "1px solid #d1d9e6",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "5px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "2px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   [theme.breakpoints.up("sm")]: {
//     maxHeight: "380px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "none",
//     overflowY: "visible",
//     overflowX: "auto",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "8px 6px",
//   fontSize: "13px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.3px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "10px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "10px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "6px 4px",
//     fontSize: "11px",
//     "&.MuiTableCell-head": {
//       fontSize: "12px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.15s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "10px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "10px",
//   },
// }));

// const ChartContainer = styled(Box)(({ theme }) => ({
//   position: "relative",
//   backgroundColor: "#f9fafc",
//   borderRadius: "10px",
//   padding: theme.spacing(1.5),
//   boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
//   border: "1px solid #e6e9ef",
//   marginBottom: theme.spacing(1.5),
//   width: "100%",
//   [theme.breakpoints.down("sm")]: {
//     padding: theme.spacing(1),
//   },
// }));

// const ChartTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "14px",
//   fontWeight: 700,
//   color: "#0e3c66",
//   marginBottom: theme.spacing(1),
//   textAlign: "center",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "12px",
//   },
// }));

// const SliderContainer = styled(Box)(({ theme }) => ({
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "space-between",
//   gap: theme.spacing(1),
//   marginBottom: theme.spacing(1),
//   width: "100%",
// }));

// const SliderButton = styled(IconButton)(({ theme }) => ({
//   backgroundColor: "#154c79",
//   color: "#ffffff",
//   "&:hover": {
//     backgroundColor: "#0e3c66",
//   },
//   "&.Mui-disabled": {
//     backgroundColor: "#cccccc",
//     color: "#666666",
//   },
//   width: "32px",
//   height: "32px",
// }));

// const CustomTooltip = ({ active, payload, language }) => {
//   if (active && payload && payload.length) {
//     const data = payload[0].payload;
//     const t = translations[language];
    
//     let rosterTypeDisplay = "";
//     let rosterTypeColor = "#000000";
    
//     if (data.rosterType === "N") {
//       rosterTypeDisplay = t.common;
//       rosterTypeColor = "#ed6c02";
//     } else if (data.rosterType === "A - Scheduled") {
//       rosterTypeDisplay = t.scheduled;
//       rosterTypeColor = "#1976d2";
//     } else if (data.rosterType === "A - Not Scheduled") {
//       rosterTypeDisplay = t.notScheduled;
//       rosterTypeColor = "#9c27b0";
//     }

//     return (
//       <Paper
//         sx={{
//           padding: "8px 10px",
//           backgroundColor: "rgba(255, 255, 255, 0.98)",
//           border: "1px solid #154c79",
//           borderRadius: "6px",
//           boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
//           minWidth: "150px",
//         }}
//       >
//         <Box sx={{ mb: 0.5 }}>
//           <Typography
//             sx={{
//               fontSize: "12px",
//               fontWeight: 800,
//               color: "#154c79",
//               textTransform: "uppercase",
//             }}
//           >
//             {data.location}
//           </Typography>
//           <Typography
//             sx={{
//               fontSize: "9px",
//               fontWeight: 600,
//               color: rosterTypeColor,
//               mt: 0.2,
//             }}
//           >
//             {rosterTypeDisplay}
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.strength}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0e3c66" }}>
//               {data.total}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.present}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32" }}>
//               {data.real}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.absent}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#F44336" }}>
//               {data.absent}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.attendanceRate}:
//             </Typography>
//             <Typography 
//               sx={{ 
//                 fontSize: "11px", 
//                 fontWeight: 700, 
//                 color: data.percentage > 70 ? "#2E7D32" : data.percentage > 50 ? "#FF9800" : "#F44336"
//               }}
//             >
//               {data.percentage}%
//             </Typography>
//           </Box>
//         </Box>
//       </Paper>
//     );
//   }
//   return null;
// };

// const RosterAttendance = () => {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { loading, data: rosterData } = useSelector((state) => state.rosterAttendance);
//   const { data: locationData } = useSelector((state) => state.locationBased);
//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [exportProgress, setExportProgress] = useState(0);
//   const [exporting, setExporting] = useState(false);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });
//   const [language, setLanguage] = useState('en');
//   const [anchorEl, setAnchorEl] = useState(null);
//   const [activeChartIndex, setActiveChartIndex] = useState(0);
  
//   const chartContainerRefs = useRef([]);
//   const t = translations[language];

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     dispatch(fetchRosterAttendance(formatDate(selectedDate)));
//     dispatch(fetchLocationBased(formatDate(selectedDate)));
//   }, [dispatch, selectedDate]);

//   const handleDateChange = (date) => {
//     setSelectedDate(date);
//     setOpenDatePicker(false);
//     const formatted = formatDate(date);
//     dispatch(fetchRosterAttendance(formatted));
//     dispatch(fetchLocationBased(formatted));
//     setActiveChartIndex(0);
//   };

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbar({
//       open: true,
//       message,
//       severity,
//     });
//   };

//   const closeSnackbar = () => {
//     setSnackbar({ ...snackbar, open: false });
//   };

//   const handleLanguageMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget);
//   };

//   const handleLanguageMenuClose = () => {
//     setAnchorEl(null);
//   };

//   const handleLanguageChange = (lang) => {
//     setLanguage(lang);
//     handleLanguageMenuClose();
//   };

//   const handlePrevChart = () => {
//     setActiveChartIndex((prev) => Math.max(0, prev - 1));
//   };

//   const handleNextChart = () => {
//     setActiveChartIndex((prev) => Math.min(chartCategories.length - 1, prev + 1));
//   };

//   // Process raw data - Filter out locations with 0% attendance
//   const processedData = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) return [];

//     return rosterData
//       .filter((item) => 
//         item.location !== "TOTAL" && 
//         Number(item.Percentage || 0) > 0
//       )
//       .map((item) => ({
//         location: item.location,
//         totalEmp: Number(item.TotalEmp || 0),
//         todayAtt: Number(item.TodayAtt || 0),
//         absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//         percentage: Math.round(Number(item.Percentage || 0)),
//         rosterType: item.Roster || "N",
//       }));
//   }, [rosterData]);

//   // Categorize data by roster type
//   const categorizedData = useMemo(() => {
//     const scheduled = processedData.filter(item => item.rosterType === "A - Scheduled");
//     const notScheduled = processedData.filter(item => item.rosterType === "A - Not Scheduled");
//     const common = processedData.filter(item => item.rosterType === "N");
    
//     return {
//       scheduled,
//       notScheduled,
//       common
//     };
//   }, [processedData]);

//   // Chart categories for slider
//   const chartCategories = useMemo(() => {
//     const categories = [
//       {
//         title: `${t.common} (${categorizedData.common.length})`,
//         data: categorizedData.common,
//         type: 'common',
//         color: '#ed6c02'
//       },
//       {
//         title: `${t.scheduled} (${categorizedData.scheduled.length})`,
//         data: categorizedData.scheduled,
//         type: 'scheduled',
//         color: '#1976d2'
//       },
//       {
//         title: `${t.notScheduled} (${categorizedData.notScheduled.length})`,
//         data: categorizedData.notScheduled,
//         type: 'notScheduled',
//         color: '#9c27b0'
//       }
//     ].filter(category => category.data.length > 0);
    
//     return categories;
//   }, [categorizedData, t]);

//   // Chart data for active category
//   const chartData = useMemo(() => {
//     if (chartCategories.length === 0 || !chartCategories[activeChartIndex]) {
//       return [];
//     }
    
//     return chartCategories[activeChartIndex].data.map((item) => ({
//       location: item.location,
//       total: item.totalEmp,
//       real: item.todayAtt,
//       absent: item.absent,
//       percentage: item.percentage,
//       rosterType: item.rosterType,
//     }));
//   }, [chartCategories, activeChartIndex]);

//   // Calculate today strength correctly: (Common TotalEmp) + (Scheduled TotalEmp)
//   const todayStrength = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) return 0;
    
//     return rosterData
//       .filter((item) => 
//         item.location !== "TOTAL" && 
//         (item.Roster === "N" || item.Roster === "A - Scheduled")
//       )
//       .reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//   }, [rosterData]);

//   // Calculate total stats
//   const totalStats = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) {
//       return {
//         total: 0,
//         present: 0,
//         absent: 0,
//         avgPercentage: 0,
//         scheduledTotal: 0,
//         scheduledPresent: 0,
//         scheduledPercentage: 0,
//         notScheduledTotal: 0,
//         notScheduledPresent: 0,
//         notScheduledPercentage: 0,
//         commonTotal: 0,
//         commonPresent: 0,
//         commonPercentage: 0,
//         scheduledCount: 0,
//         notScheduledCount: 0,
//         commonCount: 0
//       };
//     }

//     const allData = rosterData
//       .filter((item) => item.location !== "TOTAL")
//       .map((item) => ({
//         totalEmp: Number(item.TotalEmp || 0),
//         todayAtt: Number(item.TodayAtt || 0),
//         rosterType: item.Roster || "N",
//       }));
    
//     const total = allData.reduce((sum, item) => sum + item.totalEmp, 0);
//     const present = allData.reduce((sum, item) => sum + item.todayAtt, 0);
//     const absent = total - present;
//     const avgPercentage = total > 0 ? Math.round((present / total) * 100) : 0;

//     const scheduledItems = allData.filter(item => item.rosterType === "A - Scheduled");
//     const notScheduledItems = allData.filter(item => item.rosterType === "A - Not Scheduled");
//     const commonItems = allData.filter(item => item.rosterType === "N");
    
//     const scheduledTotal = scheduledItems.reduce((sum, item) => sum + item.totalEmp, 0);
//     const scheduledPresent = scheduledItems.reduce((sum, item) => sum + item.todayAtt, 0);
//     const scheduledPercentage = scheduledTotal > 0 ? Math.round((scheduledPresent / scheduledTotal) * 100) : 0;
    
//     const notScheduledTotal = notScheduledItems.reduce((sum, item) => sum + item.totalEmp, 0);
//     const notScheduledPresent = notScheduledItems.reduce((sum, item) => sum + item.todayAtt, 0);
//     const notScheduledPercentage = notScheduledTotal > 0 ? Math.round((notScheduledPresent / notScheduledTotal) * 100) : 0;
    
//     const commonTotal = commonItems.reduce((sum, item) => sum + item.totalEmp, 0);
//     const commonPresent = commonItems.reduce((sum, item) => sum + item.todayAtt, 0);
//     const commonPercentage = commonTotal > 0 ? Math.round((commonPresent / commonTotal) * 100) : 0;

//     return { 
//       total, 
//       present, 
//       absent, 
//       avgPercentage,
//       scheduledTotal,
//       scheduledPresent,
//       scheduledPercentage,
//       notScheduledTotal,
//       notScheduledPresent,
//       notScheduledPercentage,
//       commonTotal,
//       commonPresent,
//       commonPercentage,
//       scheduledCount: scheduledItems.length,
//       notScheduledCount: notScheduledItems.length,
//       commonCount: commonItems.length
//     };
//   }, [rosterData]);

//   const overallTotalStrength = useMemo(() => {
//     if (!locationData || !Array.isArray(locationData)) return 0;
//     return locationData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//   }, [locationData]);

//   // ================= PDF EXPORT FUNCTION =================
//   const handleExportPDF = async () => {
//     if (exporting) return;
    
//     try {
//       setExporting(true);
//       setExportProgress(10);
      
//       // Create PDF document
//       const doc = new jsPDF("p", "mm", "a4");
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();
      
//       // Page 1: Cover page with title and summary
//       doc.setFontSize(20);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.reportTitle, pageWidth / 2, 40, { align: "center" });
      
//       doc.setFontSize(14);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, pageWidth / 2, 60, { align: "center" });
      
//       // Summary statistics
//       doc.setFontSize(12);
//       doc.text(t.summaryStats, pageWidth / 2, 80, { align: "center" });
      
//       const summaryY = 90;
//       doc.text(`${t.totalStrength}: ${overallTotalStrength}`, 30, summaryY);
//       doc.text(`${t.todayStrength}: ${todayStrength}`, 30, summaryY + 8);
//       doc.text(`${t.todayAttendance}: ${totalStats.present} (${totalStats.avgPercentage}%)`, 30, summaryY + 16);
//       doc.text(`${t.absent}: ${totalStats.absent} (${100 - totalStats.avgPercentage}%)`, 30, summaryY + 24);
      
//       // Type breakdown
//       doc.text(`Scheduled: ${totalStats.scheduledCount} locations, ${totalStats.scheduledPresent}/${totalStats.scheduledTotal} (${totalStats.scheduledPercentage}%)`, 30, summaryY + 35);
//       doc.text(`Not Scheduled: ${totalStats.notScheduledCount} locations, ${totalStats.notScheduledPresent}/${totalStats.notScheduledTotal} (${totalStats.notScheduledPercentage}%)`, 30, summaryY + 43);
//       doc.text(`Common: ${totalStats.commonCount} locations, ${totalStats.commonPresent}/${totalStats.commonTotal} (${totalStats.commonPercentage}%)`, 30, summaryY + 51);
      
//       doc.setDrawColor(21, 76, 121);
//       doc.setLineWidth(0.5);
//       doc.line(30, summaryY + 60, pageWidth - 30, summaryY + 60);
      
//       setExportProgress(30);
      
//       // Capture and add each chart to PDF
//       for (let i = 0; i < chartCategories.length; i++) {
//         const container = chartContainerRefs.current[i];
//         const category = chartCategories[i];
        
//         if (!container || !category) continue;
        
//         // Make sure the chart is visible for capture
//         const originalDisplay = container.style.display;
//         container.style.display = 'block';
        
//         // Wait for rendering
//         await new Promise(resolve => setTimeout(resolve, 300));
        
//         // Capture chart as image
//         const chartCanvas = await html2canvas(container, {
//           scale: 2,
//           backgroundColor: "#F9FAFC",
//           logging: false,
//           useCORS: true,
//           allowTaint: false,
//         });
        
//         const chartImage = chartCanvas.toDataURL("image/png");
        
//         // Add new page for each chart
//         if (i > 0 || i === 0) {
//           doc.addPage();
//         }
        
//         // Add chart title
//         doc.setFontSize(16);
//         doc.setFont("helvetica", "bold");
//         doc.text(`${category.title} - ${t.chartViz}`, 20, 20);
        
//         doc.setFontSize(12);
//         doc.setFont("helvetica", "normal");
//         doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
        
//         // Add chart image
//         const chartWidth = pageWidth - 40;
//         const chartHeight = (chartCanvas.height * chartWidth) / chartCanvas.width;
//         let chartY = 45;
        
//         if (chartHeight > pageHeight - chartY - 20) {
//           const maxHeight = pageHeight - chartY - 20;
//           const scaledWidth = (chartCanvas.width * maxHeight) / chartCanvas.height;
//           const chartX = (pageWidth - scaledWidth) / 2;
//           doc.addImage(chartImage, "PNG", chartX, chartY, scaledWidth, maxHeight);
//         } else {
//           const chartX = (pageWidth - chartWidth) / 2;
//           doc.addImage(chartImage, "PNG", chartX, chartY, chartWidth, chartHeight);
//         }
        
//         // Restore original display
//         if (i !== activeChartIndex) {
//           container.style.display = originalDisplay;
//         }
        
//         setExportProgress(30 + Math.floor((i + 1) / chartCategories.length * 40));
//       }
      
//       setExportProgress(70);
      
//       // Final page: Detailed Table
//       doc.addPage();
      
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.detailedData, 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
      
//       // Prepare table data
//       const allDataForTable = rosterData
//         .filter((item) => item.location !== "TOTAL")
//         .map((item) => ({
//           location: item.location,
//           totalEmp: Number(item.TotalEmp || 0),
//           todayAtt: Number(item.TodayAtt || 0),
//           absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//           percentage: Math.round(Number(item.Percentage || 0)),
//           rosterType: item.Roster || "N",
//         }));
      
//       const tableData = allDataForTable.map((item) => {
//         let typeDisplay = "";
//         if (item.rosterType === "N") typeDisplay = t.common;
//         else if (item.rosterType === "A - Scheduled") typeDisplay = t.scheduled;
//         else if (item.rosterType === "A - Not Scheduled") typeDisplay = t.notScheduled;
        
//         return [
//           item.location,
//           typeDisplay,
//           item.totalEmp.toString(),
//           item.todayAtt.toString(),
//           item.absent.toString(),
//           `${item.percentage}%`,
//         ];
//       });
      
//       tableData.push([
//         t.total,
//         `${totalStats.scheduledCount}S/${totalStats.notScheduledCount}NS/${totalStats.commonCount}C`,
//         totalStats.total.toString(),
//         totalStats.present.toString(),
//         totalStats.absent.toString(),
//         `${totalStats.avgPercentage}%`,
//       ]);
      
//       setExportProgress(85);
      
//       autoTable(doc, {
//         startY: 35,
//         head: [[t.location, t.rosterType, t.strength, t.todayAttendance, t.absent, t.attendanceRate]],
//         body: tableData,
//         styles: {
//           fontSize: 9,
//           cellPadding: 4,
//         },
//         headStyles: {
//           fillColor: [21, 76, 121],
//           textColor: 255,
//           fontStyle: "bold",
//         },
//         alternateRowStyles: {
//           fillColor: [245, 248, 252],
//         },
//       });
      
//       // Footer on all pages
//       const pageCount = doc.internal.getNumberOfPages();
//       for (let i = 1; i <= pageCount; i++) {
//         doc.setPage(i);
//         doc.setFontSize(10);
//         doc.setTextColor(128, 128, 128);
//         doc.text(
//           `${t.page} ${i} ${t.of} ${pageCount}`,
//           pageWidth - 20,
//           pageHeight - 10,
//           { align: "right" }
//         );
//         doc.text(
//           `${t.generatedOn} ${new Date().toLocaleDateString()}`,
//           20,
//           pageHeight - 10
//         );
//       }

//       setExportProgress(100);
      
//       // Save the PDF
//       const filename = `Roster_Attendance_Report_${formatDate(selectedDate)}.pdf`;
//       doc.save(filename);
      
//       showSnackbar(t.pdfSuccess, "success");
      
//       setTimeout(() => {
//         setExporting(false);
//         setExportProgress(0);
//       }, 1000);
      
//     } catch (error) {
//       console.error("Error generating PDF:", error);
//       showSnackbar(t.pdfError, "error");
//       setExporting(false);
//       setExportProgress(0);
//     }
//   };

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         {/* Export Progress Backdrop */}
//         <Backdrop
//           sx={{
//             color: "#fff",
//             zIndex: (theme) => theme.zIndex.drawer + 1,
//             backgroundColor: "rgba(0, 0, 0, 0.7)",
//           }}
//           open={exporting}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//               gap: 2,
//               p: 4,
//               bgcolor: "background.paper",
//               borderRadius: 2,
//               boxShadow: 24,
//               maxWidth: 400,
//               width: "100%",
//             }}
//           >
//             <CircularProgress
//               size={60}
//               thickness={4}
//               sx={{ color: "#154c79" }}
//             />
//             <Typography variant="h6" color="primary" fontWeight="bold">
//               {exporting ? t.exporting : t.exportPdf}
//             </Typography>
//             <Box sx={{ width: "100%", mt: 2 }}>
//               <LinearProgress
//                 variant="determinate"
//                 value={exportProgress}
//                 sx={{
//                   height: 10,
//                   borderRadius: 5,
//                   backgroundColor: "#e0e0e0",
//                   "& .MuiLinearProgress-bar": {
//                     backgroundColor: "#2e7d32",
//                   },
//                 }}
//               />
//               <Typography
//                 variant="body2"
//                 color="text.secondary"
//                 align="center"
//                 mt={1}
//               >
//                 {exportProgress}% Complete
//               </Typography>
//             </Box>
//             <Typography variant="body2" color="text.secondary" align="center">
//               {t.prepareReport}
//             </Typography>
//           </Box>
//         </Backdrop>

//         {/* Success/Error Snackbar */}
//         <Snackbar
//           open={snackbar.open}
//           autoHideDuration={4000}
//           onClose={closeSnackbar}
//           anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//         >
//           <Alert
//             onClose={closeSnackbar}
//             severity={snackbar.severity}
//             sx={{ width: "100%" }}
//             elevation={6}
//             variant="filled"
//           >
//             {snackbar.message}
//           </Alert>
//         </Snackbar>

//         {/* Header Section */}
//         <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
//           <Typography
//             sx={{
//               fontSize: "16px",
//               fontWeight: 700,
//               color: "#0e3c66",
//               textAlign: "center",
//               flex: 1,
//             }}
//           >
//             {t.title}
//           </Typography>
          
//           {/* Language Selector */}
//           <Tooltip title={t.language}>
//             <IconButton
//               onClick={handleLanguageMenuOpen}
//               sx={{
//                 backgroundColor: language === 'si' ? '#142c54' : '#f0f0f0',
//                 color: language === 'si' ? 'white' : '#142c54',
//                 '&:hover': {
//                   backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
//                 },
//                 width: '36px',
//                 height: '36px',
//                 ml: 1,
//               }}
//             >
//               {language === 'si' ? (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
//               ) : (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
//               )}
//             </IconButton>
//           </Tooltip>
          
//           <Menu
//             anchorEl={anchorEl}
//             open={Boolean(anchorEl)}
//             onClose={handleLanguageMenuClose}
//             PaperProps={{
//               sx: {
//                 mt: 1,
//                 minWidth: 120,
//                 borderRadius: '8px',
//                 boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
//               }
//             }}
//           >
//             <MenuItem 
//               onClick={() => handleLanguageChange('en')}
//               selected={language === 'en'}
//               sx={{
//                 backgroundColor: language === 'en' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <LanguageIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
//                   English
//                 </Typography>
//               </Box>
//             </MenuItem>
//             <MenuItem 
//               onClick={() => handleLanguageChange('si')}
//               selected={language === 'si'}
//               sx={{
//                 backgroundColor: language === 'si' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <TranslateIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
//                   සිංහල
//                 </Typography>
//               </Box>
//             </MenuItem>
//           </Menu>
//         </Box>

//         {/* Date Picker and Export Button */}
//         <Box
//           sx={{
//             display: "flex",
//             gap: 1,
//             mb: 1.5,
//             alignItems: "center",
//             flexWrap: isMobile ? "wrap" : "nowrap",
//           }}
//         >
//           <Button
//             variant="contained"
//             startIcon={<CalendarTodayIcon />}
//             onClick={() => setOpenDatePicker(true)}
//             disabled={exporting}
//             sx={{
//               flex: isMobile ? 1 : 3,
//               minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//               height: "38px",
//               borderRadius: "8px",
//               backgroundColor: exporting ? "#9e9e9e" : "#142c54",
//               color: "#fff",
//               fontWeight: "bold",
//               textTransform: "none",
//               fontSize: "13px",
//               "&:hover": { 
//                 backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
//               },
//             }}
//           >
//             {formatDate(selectedDate)}
//           </Button>

//           {!loading && processedData.length > 0 && (
//             <Button
//               variant="contained"
//               startIcon={<PictureAsPdfIcon />}
//               onClick={handleExportPDF}
//               disabled={exporting || chartData.length === 0}
//               sx={{
//                 flex: 1,
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//                 height: "38px",
//                 borderRadius: "8px",
//                 backgroundColor: exporting 
//                   ? "#9e9e9e" 
//                   : (chartData.length === 0 ? "#bdbdbd" : "#2e7d32"),
//                 color: "#fff",
//                 fontWeight: "bold",
//                 textTransform: "none",
//                 fontSize: "13px",
//                 "&:hover": { 
//                   backgroundColor: exporting 
//                     ? "#9e9e9e" 
//                     : (chartData.length === 0 ? "#bdbdbd" : "#1b5e20") 
//                 },
//               }}
//             >
//               {exporting ? t.exporting : t.exportPdf}
//             </Button>
//           )}
//         </Box>

//         {openDatePicker && (
//           <DatePicker
//             open={openDatePicker}
//             value={selectedDate}
//             onChange={handleDateChange}
//             onClose={() => setOpenDatePicker(false)}
//             renderInput={(params) => (
//               <TextField {...params} sx={{ display: "none" }} />
//             )}
//           />
//         )}

//         {/* Sticky Cards Section */}
//         {!loading && processedData.length > 0 && (
//           <StickyCardsContainer>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: "6px",
//                 mb: 1,
//               }}
//             >
//               {/* Total Strength Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                   border: "1px solid #aec4e0",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0E3C66",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.totalStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {overallTotalStrength}
//                 </Typography>
//               </Box>

//               {/* Today Strength Card - CORRECTED */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
//                   border: "1px solid #ffb74d",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#e65100",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.todayStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {todayStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500 }}>
//                   {totalStats.scheduledCount + totalStats.commonCount} Locations
//                 </Typography>
//               </Box>

//               {/* Today Attendance Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//                   border: "1px solid #80cbc4",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#004d40",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.todayAttendance}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {totalStats.present}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color:
//                       totalStats.avgPercentage > 70
//                         ? "#2E7D32"
//                         : totalStats.avgPercentage > 50
//                         ? "#FF9800"
//                         : "#D32F2F",
//                   }}
//                 >
//                   ({totalStats.avgPercentage}%)
//                 </Typography>
//               </Box>
//             </Box>

//             {/* Type Summary */}
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: "10px",
//                 mb: 1,
//                 p: 1,
//                 backgroundColor: "#f5f5f5",
//                 borderRadius: "6px",
//                 flexWrap: "wrap",
//               }}
//             >
//               <Chip
//                 label={`${t.common}: ${totalStats.commonCount}`}
//                 size="small"
//                 sx={{ backgroundColor: "#ed6c02", color: "white", fontWeight: 600 }}
//               />
//               <Chip
//                 label={`${t.scheduled}: ${totalStats.scheduledCount}`}
//                 size="small"
//                 sx={{ backgroundColor: "#1976d2", color: "white", fontWeight: 600 }}
//               />
//               <Chip
//                 label={`${t.notScheduled}: ${totalStats.notScheduledCount}`}
//                 size="small"
//                 sx={{ backgroundColor: "#9c27b0", color: "white", fontWeight: 600 }}
//               />
//             </Box>
//           </StickyCardsContainer>
//         )}

//         {/* Content Container */}
//         <Box
//           sx={{
//             [theme.breakpoints.down("sm")]: {
//               maxHeight: "calc(100vh - 300px)",
//               overflowY: "auto",
//             },
//           }}
//         >
//           {/* Chart Section with Slider */}
//           {!loading && chartCategories.length > 0 && chartData.length > 0 && (
//             <>
//               {chartCategories.map((category, index) => (
//                 <ChartContainer 
//                   key={index} 
//                   ref={el => chartContainerRefs.current[index] = el}
//                   sx={{ 
//                     display: index === activeChartIndex ? 'block' : 'none',
//                   }}
//                 >
//                   <SliderContainer>
//                     <SliderButton 
//                       onClick={handlePrevChart} 
//                       disabled={activeChartIndex === 0}
//                       size="small"
//                     >
//                       <ArrowBackIosIcon fontSize="small" />
//                     </SliderButton>
                    
//                     <ChartTitle>
//                       {category.title}
//                     </ChartTitle>
                    
//                     <SliderButton 
//                       onClick={handleNextChart} 
//                       disabled={activeChartIndex === chartCategories.length - 1}
//                       size="small"
//                     >
//                       <ArrowForwardIosIcon fontSize="small" />
//                     </SliderButton>
//                   </SliderContainer>

//                   <Box sx={{ width: "100%", height: isMobile ? 450 : 420 }}>
//                     <ResponsiveContainer width="100%" height="100%">
//                       <BarChart
//                         data={chartData}
//                         layout="vertical"
//                         barSize={isMobile ? 24 : 28}
//                         margin={{
//                           top: 5,
//                           right: isMobile ? 10 : 30,
//                           left: 0,
//                           bottom: 5,
//                         }}
//                       >
//                         <CartesianGrid
//                           strokeDasharray="2 2"
//                           stroke="#e0e0e0"
//                           horizontal={true}
//                           vertical={false}
//                         />
                        
//                         <YAxis
//                           dataKey="location"
//                           type="category"
//                           axisLine={false}
//                           tickLine={false}
//                           tick={{
//                             fontSize: isMobile ? 10 : 11,
//                             fontWeight: 600,
//                             fill: "#333",
//                             textAnchor: "start",
//                             dx: -150,
//                           }}
//                           width={isMobile ? 170 : 180}
//                           interval={0}
//                           tickFormatter={(value) => value}
//                         />

//                         <XAxis
//                           type="number"
//                           tick={{ fontSize: isMobile ? 9 : 10 }}
//                           axisLine={false}
//                           tickLine={false}
//                           domain={[0, 100]}
//                           ticks={[0, 20, 40, 60, 80, 100]}
//                           tickFormatter={(value) => `${value}%`}
//                         />

//                         <RechartsTooltip
//                           content={<CustomTooltip language={language} />}
//                           cursor={{ fill: "rgba(0,0,0,0.03)" }}
//                         />

//                         <Bar
//                           dataKey="percentage"
//                           name="Attendance Percentage"
//                           radius={[0, 10, 10, 0]}
//                         >
//                           {chartData.map((entry, index) => (
//                             <Cell
//                               key={`attendance-cell-${index}`}
//                               fill={
//                                 entry.percentage > 70
//                                   ? "#4CAF50"
//                                   : entry.percentage > 50
//                                   ? "#FF9800"
//                                   : "#F44336"
//                               }
//                             />
//                           ))}

//                           <LabelList
//                             dataKey="real"
//                             position="insideLeft"
//                             style={{
//                               fontSize: isMobile ? 9 : 10,
//                               fontWeight: 700,
//                               fill: "#fff",
//                             }}
//                             offset={12}
//                           />

//                           <LabelList
//                             dataKey="percentage"
//                             position="right"
//                             formatter={(value) => `${value}%`}
//                             style={{
//                               fontSize: isMobile ? 10 : 11,
//                               fontWeight: 600,
//                               fill: "#555",
//                             }}
//                             offset={8}
//                           />
//                         </Bar>
//                       </BarChart>
//                     </ResponsiveContainer>
//                   </Box>

//                   {/* Color Legend */}
//                   <Box
//                     sx={{
//                       display: "flex",
//                       justifyContent: "center",
//                       gap: "15px",
//                       mt: 1,
//                       flexWrap: "wrap",
//                     }}
//                   >
//                     <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                       <Box
//                         sx={{
//                           width: "14px",
//                           height: "14px",
//                           backgroundColor: "#4CAF50",
//                           borderRadius: "3px",
//                         }}
//                       />
//                       <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                         {t.highAttendance} ({'>'}70%)
//                       </Typography>
//                     </Box>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                       <Box
//                         sx={{
//                           width: "14px",
//                           height: "14px",
//                           backgroundColor: "#FF9800",
//                           borderRadius: "3px",
//                         }}
//                       />
//                       <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                         {t.mediumAttendance} (50-70%)
//                       </Typography>
//                     </Box>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                       <Box
//                         sx={{
//                           width: "14px",
//                           height: "14px",
//                           backgroundColor: "#F44336",
//                           borderRadius: "3px",
//                         }}
//                       />
//                       <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                         {t.lowAttendance} ({'<'}50%)
//                       </Typography>
//                     </Box>
//                   </Box>
//                 </ChartContainer>
//               ))}
//             </>
//           )}

//           {/* TABLE SECTION */}
//           {!loading && rosterData && rosterData.filter(item => item.location !== "TOTAL").length > 0 && (
//             <StyledTableContainer component={Paper}>
//               <Table stickyHeader>
//                 <TableHead>
//                   <TableRow>
//                     <StyledTableCell>{t.location}</StyledTableCell>
//                     <StyledTableCell>{t.rosterType}</StyledTableCell>
//                     <StyledTableCell>{t.strength}</StyledTableCell>
//                     <StyledTableCell>{t.todayAttendance}</StyledTableCell>
//                     <StyledTableCell>{t.absent}</StyledTableCell>
//                     <StyledTableCell>{t.attendanceRate}</StyledTableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {rosterData
//                     .filter((item) => item.location !== "TOTAL")
//                     .map((row, index) => {
//                       const totalEmp = Number(row.TotalEmp || 0);
//                       const todayAtt = Number(row.TodayAtt || 0);
//                       const percentage = Math.round(Number(row.Percentage || 0));
//                       const rosterType = row.Roster || "N";
                      
//                       let typeDisplay = "";
//                       let typeColor = "";
//                       if (rosterType === "N") {
//                         typeDisplay = t.common;
//                         typeColor = "#ed6c02";
//                       } else if (rosterType === "A - Scheduled") {
//                         typeDisplay = t.scheduled;
//                         typeColor = "#1976d2";
//                       } else if (rosterType === "A - Not Scheduled") {
//                         typeDisplay = t.notScheduled;
//                         typeColor = "#9c27b0";
//                       }
                      
//                       return (
//                         <StyledTableRow key={index}>
//                           <StyledTableCell
//                             sx={{
//                               textAlign: "left",
//                               pl: isMobile ? 2 : 3,
//                               maxWidth: "200px",
//                               overflow: "hidden",
//                               textOverflow: "ellipsis",
//                               whiteSpace: "nowrap",
//                               color: typeColor,
//                               fontWeight: 600,
//                             }}
//                           >
//                             {row.location}
//                           </StyledTableCell>
//                           <StyledTableCell>
//                             <Chip
//                               label={typeDisplay}
//                               size="small"
//                               sx={{
//                                 backgroundColor: `${typeColor}20`,
//                                 color: typeColor,
//                                 fontWeight: 600,
//                                 fontSize: "11px",
//                               }}
//                             />
//                           </StyledTableCell>
//                           <StyledTableCell>{totalEmp}</StyledTableCell>
//                           <StyledTableCell sx={{ color: "#2E7D32", fontWeight: 600 }}>
//                             {todayAtt}
//                           </StyledTableCell>
//                           <StyledTableCell sx={{ color: "#D32F2F", fontWeight: 600 }}>
//                             {totalEmp - todayAtt}
//                           </StyledTableCell>
//                           <StyledTableCell>
//                             <Box
//                               sx={{
//                                 display: "inline-flex",
//                                 alignItems: "center",
//                                 gap: 0.5,
//                               }}
//                             >
//                               <span>{percentage}%</span>
//                               <Box
//                                 sx={{
//                                   width: "6px",
//                                   height: "6px",
//                                   borderRadius: "50%",
//                                   backgroundColor:
//                                     percentage > 70
//                                       ? "#4CAF50"
//                                       : percentage > 50
//                                       ? "#FF9800"
//                                       : "#F44336",
//                                 }}
//                               />
//                             </Box>
//                           </StyledTableCell>
//                         </StyledTableRow>
//                       );
//                     })}
//                   <StyledTableRow
//                     sx={{
//                       backgroundColor: "#154c79 !important",
//                       "&:hover": { backgroundColor: "#154c79 !important" },
//                     }}
//                   >
//                     <StyledTableCell
//                       sx={{
//                         color: "#ffffff",
//                         fontWeight: 700,
//                         textAlign: "left",
//                         pl: isMobile ? 2 : 3,
//                       }}
//                     >
//                       {t.total}
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       {totalStats.scheduledCount}S/{totalStats.notScheduledCount}NS/{totalStats.commonCount}C
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       {totalStats.total}
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       {totalStats.present}
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       {totalStats.absent}
//                     </StyledTableCell>
//                     <StyledTableCell
//                       sx={{ color: "#ffffff", fontWeight: 700 }}
//                     >
//                       <Box
//                         sx={{
//                           display: "inline-flex",
//                           alignItems: "center",
//                           gap: 0.5,
//                         }}
//                       >
//                         <span>{totalStats.avgPercentage}%</span>
//                         <Box
//                           sx={{
//                             width: "6px",
//                             height: "6px",
//                             borderRadius: "50%",
//                             backgroundColor:
//                               totalStats.avgPercentage > 70
//                                 ? "#4CAF50"
//                                 : totalStats.avgPercentage > 50
//                                 ? "#FF9800"
//                                 : "#F44336",
//                           }}
//                         />
//                       </Box>
//                     </StyledTableCell>
//                   </StyledTableRow>
//                 </TableBody>
//               </Table>
//             </StyledTableContainer>
//           )}

//           {/* Loading State */}
//           {loading && (
//             <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
//               <CircularProgress />
//             </Box>
//           )}

//           {/* No Data State */}
//           {!loading && (!rosterData || rosterData.filter(item => item.location !== "TOTAL").length === 0) && (
//             <Box sx={{ textAlign: "center", py: 4 }}>
//               <Typography color="textSecondary">{t.noData}</Typography>
//             </Box>
//           )}
//         </Box>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// };

// export default RosterAttendance;








// import React, { useEffect, useState, useMemo, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchRosterAttendance } from "../../actions/RosterAttendanceAction";
// import { fetchLocationBased } from "../../actions/LocationBasedAction";
// import {
//   Box,
//   CircularProgress,
//   Typography,
//   Paper,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   useTheme,
//   useMediaQuery,
//   Button,
//   Snackbar,
//   Alert,
//   LinearProgress,
//   Backdrop,
//   IconButton,
//   Menu,
//   MenuItem,
//   Tooltip,
//   TextField,
//   Chip,
// } from "@mui/material";
// import { format } from "date-fns";
// import { useLocation } from "react-router-dom";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   ResponsiveContainer,
//   LabelList,
//   Cell,
//   Tooltip as RechartsTooltip,
// } from "recharts";
// import { styled } from "@mui/material/styles";
// import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import TranslateIcon from "@mui/icons-material/Translate";
// import LanguageIcon from "@mui/icons-material/Language";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import html2canvas from "html2canvas";

// // ------------ Translation Texts ------------
// const translations = {
//   en: {
//     title: "Roster Attendance",
//     datePicker: "Select Date",
//     exportPdf: "Export Full PDF",
//     exporting: "Exporting...",
//     totalStrength: "Total Strength",
//     scheduledStrength: "Today Strength (Roster)",
//     todayStrength: "Today Strength",
//     todayAttendance: "Today Attendance",
//     present: "Present",
//     absent: "Absent",
//     location: "Location",
//     strength: "Strength",
//     attendanceRate: "Attendance Rate (%)",
//     chartTitle: "Roster Attendance by Location",
//     locationsWithAttendance: "locations with attendance",
//     highAttendance: "High Attendance",
//     mediumAttendance: "Medium",
//     lowAttendance: "Low",
//     loading: "Loading roster data...",
//     noData: "No roster attendance data available for the selected date.",
//     total: "Total",
//     reportTitle: "Roster Attendance Report",
//     reportDate: "Report Date",
//     summaryStats: "Summary Statistics",
//     chartViz: "Roster Attendance Visualization by Location",
//     detailedData: "Detailed Roster Attendance Data",
//     page: "Page",
//     of: "of",
//     generatedOn: "Generated on",
//     pdfSuccess: "PDF exported successfully!",
//     pdfError: "Failed to export PDF. Please try again.",
//     prepareReport: "Preparing your report with chart and table data...",
//     language: "Language",
//     roster: "Roster",
//     common: "Common",
//     rosterLocations: "Roster Locations",
//     commonLocations: "Common Locations",
//   },
//   si: {
//     title: "රෝස්ටර් පැමිණීම්",
//     datePicker: "දිනය තෝරන්න",
//     exportPdf: "PDF බාගත කරන්න",
//     exporting: "PDF බාගත කරමින්...",
//     totalStrength: "මුළු සේවක පිරිස",
//     scheduledStrength: "අද නියමිත පැමිණීම් ශක්තිය",
//     todayStrength: "අද සේවක පිරිස",
//     todayAttendance: "අද පැමිණීම්",
//     present: "පැමිණි සේවකයන්",
//     absent: "පැමිණ නොමැති සේවකයන්",
//     location: "ස්ථානය",
//     strength: "සේවක පිරිස",
//     attendanceRate: "පැමිණීම් අනුපාතය (%)",
//     chartTitle: "ස්ථානය අනුව රෝස්ටර් පැමිණීම්",
//     locationsWithAttendance: "පැමිණීම් සහිත ස්ථාන",
//     highAttendance: "ඉහළ පැමිණීම් අනුපාතය",
//     mediumAttendance: "මධ්‍යම පැමිණීම් අනුපාතය",
//     lowAttendance: "අඩු පැමිණීම් අනුපාතය",
//     loading: "රෝස්ටර් දත්ත ලබා ගනිමින්...",
//     noData: "තෝරාගත් දිනය සඳහා රෝස්ටර් පැමිණීම් දත්ත නොමැත.",
//     total: "සියල්ල",
//     reportTitle: "රෝස්ටර් පැමිණීම් වාර්තාව",
//     reportDate: "වාර්තා දිනය",
//     summaryStats: "සාරාංශ සංඛ්‍යාලේඛන",
//     chartViz: "ස්ථානය අනුව රෝස්ටර් පැමිණීම් දෘශ්‍යකරණය",
//     detailedData: "සවිස්තරාත්මක රෝස්ටර් පැමිණීම් දත්ත",
//     page: "පිටුව",
//     of: "යි",
//     generatedOn: "සෑදූ දිනය",
//     pdfSuccess: "PDF ගොනුව සාර්ථකව බාගත කරන ලදී!",
//     pdfError: "PDF ගොනුව බාගත කිරීම අසාර්ථක විය. කරුණාකර නැවත උත්සාහ කරන්න.",
//     prepareReport: "ප්‍රස්ථාරය සහ වගු දත්ත සමඟ ඔබේ වාර්තාව සකස් කරමින්...",
//     language: "භාෂාව",
//     roster: "රෝස්ටර්",
//     common: "පොදු",
//     rosterLocations: "රෝස්ටර් ස්ථාන",
//     commonLocations: "පොදු ස්ථාන",
//   }
// };

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "1000px",
//   margin: "0 auto",
//   padding: "8px 12px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     padding: "0px 8px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "10px",
//   boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
//   border: "1px solid #d1d9e6",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "5px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "2px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   [theme.breakpoints.up("sm")]: {
//     maxHeight: "380px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "none",
//     overflowY: "visible",
//     overflowX: "auto",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "8px 6px",
//   fontSize: "13px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.3px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "10px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "10px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "6px 4px",
//     fontSize: "11px",
//     "&.MuiTableCell-head": {
//       fontSize: "12px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.15s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "10px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "10px",
//   },
// }));

// const ChartContainer = styled(Box)(({ theme }) => ({
//   position: "relative",
//   backgroundColor: "#f9fafc",
//   borderRadius: "10px",
//   padding: theme.spacing(1.5),
//   boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
//   border: "1px solid #e6e9ef",
//   marginBottom: theme.spacing(1.5),
//   [theme.breakpoints.down("sm")]: {
//     padding: theme.spacing(1),
//   },
// }));

// const ChartTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "14px",
//   fontWeight: 700,
//   color: "#0e3c66",
//   marginBottom: theme.spacing(1),
//   textAlign: "center",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "12px",
//   },
// }));

// const CustomTooltip = ({ active, payload, language }) => {
//   if (active && payload && payload.length) {
//     const data = payload[0].payload;
//     const t = translations[language];
    
//     return (
//       <Paper
//         sx={{
//           padding: "8px 10px",
//           backgroundColor: "rgba(255, 255, 255, 0.98)",
//           border: "1px solid #154c79",
//           borderRadius: "6px",
//           boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
//           minWidth: "150px",
//         }}
//       >
//         <Box sx={{ mb: 0.5 }}>
//           <Typography
//             sx={{
//               fontSize: "12px",
//               fontWeight: 800,
//               color: "#154c79",
//               textTransform: "uppercase",
//             }}
//           >
//             {data.location}
//           </Typography>
//           <Typography
//             sx={{
//               fontSize: "9px",
//               fontWeight: 600,
//               color: data.rosterType === "A" ? "#1976d2" : "#000000",
//               mt: 0.2,
//             }}
//           >
//             {data.rosterType === "A" ? "Roster Location" : "Common Location"}
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.strength}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0e3c66" }}>
//               {data.total}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.present}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32" }}>
//               {data.real}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.absent}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#F44336" }}>
//               {data.absent}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.attendanceRate}:
//             </Typography>
//             <Typography 
//               sx={{ 
//                 fontSize: "11px", 
//                 fontWeight: 700, 
//                 color: data.percentage > 70 ? "#2E7D32" : data.percentage > 50 ? "#FF9800" : "#F44336"
//               }}
//             >
//               {data.percentage}%
//             </Typography>
//           </Box>
//         </Box>
//       </Paper>
//     );
//   }
//   return null;
// };

// const RosterAttendance = () => {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { loading, data: rosterData } = useSelector((state) => state.rosterAttendance);
//   const { data: locationData } = useSelector((state) => state.locationBased);
//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [exportProgress, setExportProgress] = useState(0);
//   const [exporting, setExporting] = useState(false);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });
//   const [language, setLanguage] = useState('en');
//   const [anchorEl, setAnchorEl] = useState(null);
//   const location = useLocation();
  
//   const chartContainerRef = useRef(null);
//   const t = translations[language];

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     dispatch(fetchRosterAttendance(formatDate(selectedDate)));
//     dispatch(fetchLocationBased(formatDate(selectedDate)));
//   }, [dispatch, selectedDate]);

//   const handleDateChange = (date) => {
//     setSelectedDate(date);
//     setOpenDatePicker(false);
//     const formatted = formatDate(date);
//     dispatch(fetchRosterAttendance(formatted));
//     dispatch(fetchLocationBased(formatted));
//   };

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbar({
//       open: true,
//       message,
//       severity,
//     });
//   };

//   const closeSnackbar = () => {
//     setSnackbar({ ...snackbar, open: false });
//   };

//   const handleLanguageMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget);
//   };

//   const handleLanguageMenuClose = () => {
//     setAnchorEl(null);
//   };

//   const handleLanguageChange = (lang) => {
//     setLanguage(lang);
//     handleLanguageMenuClose();
//   };

//   const processedData = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) return [];

//     return rosterData
//       .filter((item) => item.location !== "TOTAL" && Number(item.TodayAtt || 0) > 0)
//       .map((item) => ({
//         location: item.location,
//         totalEmp: Number(item.TotalEmp || 0),
//         todayAtt: Number(item.TodayAtt || 0),
//         absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//         percentage: Math.round(Number(item.Percentage || 0)),
//         rosterType: item.Roster || "N",
//       }));
//   }, [rosterData]);

//   const chartData = useMemo(() => {
//     return processedData.map((item) => ({
//       location: item.location,
//       total: item.totalEmp,
//       real: item.todayAtt,
//       absent: item.absent,
//       percentage: item.percentage,
//       rosterType: item.rosterType,
//       barSize: item.percentage,
//     }));
//   }, [processedData]);

//   const totalStats = useMemo(() => {
//     // Calculate totals from ALL locations (unfiltered rosterData) for accurate footer totals
//     if (!rosterData || !Array.isArray(rosterData)) {
//       return {
//         total: 0,
//         present: 0,
//         absent: 0,
//         avgPercentage: 0,
//         rosterTotal: 0,
//         rosterPresent: 0,
//         rosterPercentage: 0,
//         commonTotal: 0,
//         commonPresent: 0,
//         commonPercentage: 0,
//         rosterCount: 0,
//         commonCount: 0
//       };
//     }

//     // Calculate from unfiltered rosterData to include all locations (even with 0 attendance)
//     // Exclude "TOTAL" row from API response
//     const filteredData = rosterData.filter(item => item.location !== "TOTAL");
    
//     const total = filteredData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//     const present = filteredData.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0);
//     const absent = total - present;
//     const avgPercentage = total > 0 ? Math.round((present / total) * 100) : 0;

//     // Calculate roster and common type totals from unfiltered data (excluding TOTAL row)
//     const rosterItems = filteredData.filter(item => item.Roster === "A");
//     const commonItems = filteredData.filter(item => item.Roster === "N");
    
//     const rosterTotal = rosterItems.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//     const rosterPresent = rosterItems.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0);
//     const rosterPercentage = rosterTotal > 0 ? Math.round((rosterPresent / rosterTotal) * 100) : 0;
    
//     const commonTotal = commonItems.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//     const commonPresent = commonItems.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0);
//     const commonPercentage = commonTotal > 0 ? Math.round((commonPresent / commonTotal) * 100) : 0;

//     // Calculate scheduled strength: Roster === "N" OR Roster === "A - Scheduled"
//     // filteredData already excludes "TOTAL" row
//     const scheduledItems = filteredData.filter(item => 
//       item.Roster === "N" || item.Roster === "A - Scheduled"
//     );
//     const scheduledStrength = scheduledItems.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);

//     // Use processedData only for counting displayed rows
//     const displayedRosterCount = processedData.filter(item => item.rosterType === "A").length;
//     const displayedCommonCount = processedData.filter(item => item.rosterType === "N").length;

//     return { 
//       total, 
//       present, 
//       absent, 
//       avgPercentage,
//       scheduledStrength,
//       rosterTotal,
//       rosterPresent,
//       rosterPercentage,
//       commonTotal,
//       commonPresent,
//       commonPercentage,
//       rosterCount: displayedRosterCount,
//       commonCount: displayedCommonCount
//     };
//   }, [rosterData, processedData]);

//   const overallTotalStrength = useMemo(() => {
//     if (!locationData || !Array.isArray(locationData)) return 0;
//     return locationData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//   }, [locationData]);

//   // Custom YAxis tick component to render colored location names
//   const renderColoredLocationTick = (props) => {
//     const { x, y, payload, data } = props;
//     const locationData = data?.find(item => item.location === payload.value);
//     const rosterType = locationData?.rosterType || "N";
    
//     return (
//       <g transform={`translate(${x},${y})`}>
//         <text
//           x={-150}
//           y={0}
//           dy={4}
//           textAnchor="start"
//           fill={rosterType === "A" ? "#1976d2" : "#000000"}
//           fontSize={isMobile ? 10 : 11}
//           fontWeight={600}
//         >
//           {payload.value}
//         </text>
//       </g>
//     );
//   };

//   // ================= PDF EXPORT FUNCTION =================
//   const handleExportPDF = async () => {
//     if (!chartContainerRef.current || exporting) return;
    
//     try {
//       setExporting(true);
//       setExportProgress(10);
      
//       // Step 1: Capture chart as image
//       setExportProgress(30);
//       const chartCanvas = await html2canvas(chartContainerRef.current, {
//         scale: 2,
//         backgroundColor: "#F9FAFC",
//         logging: false,
//         useCORS: true,
//       });
//       const chartImage = chartCanvas.toDataURL("image/png");
      
//       setExportProgress(60);
      
//       // Step 2: Create PDF document
//       const doc = new jsPDF("p", "mm", "a4");
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();
      
//       // Page 1: Cover page with title and summary
//       doc.setFontSize(20);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.reportTitle, pageWidth / 2, 40, { align: "center" });
      
//       doc.setFontSize(14);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, pageWidth / 2, 60, { align: "center" });
      
//       // Summary statistics
//       doc.setFontSize(12);
//       doc.text(t.summaryStats, pageWidth / 2, 80, { align: "center" });
      
//       const summaryY = 90;
//       doc.text(`${t.totalStrength}: ${overallTotalStrength}`, 30, summaryY);
//       doc.text(`${t.todayStrength}: ${totalStats.total}`, 30, summaryY + 8);
//       doc.text(`${t.todayAttendance}: ${totalStats.present} (${totalStats.avgPercentage}%)`, 30, summaryY + 16);
//       doc.text(`${t.absent}: ${totalStats.absent} (${100 - totalStats.avgPercentage}%)`, 30, summaryY + 24);
      
//       // Add decorative line
//       doc.setDrawColor(21, 76, 121);
//       doc.setLineWidth(0.5);
//       doc.line(30, summaryY + 45, pageWidth - 30, summaryY + 45);
      
//       setExportProgress(70);
      
//       // Page 2: Chart Visualization
//       doc.addPage();
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.chartViz, 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
//       doc.text(`${t.chartTitle} (${chartData.length} ${t.locationsWithAttendance})`, 20, 34);
      
//       // Add chart image
//       const chartWidth = pageWidth - 40;
//       const chartHeight = (chartCanvas.height * chartWidth) / chartCanvas.width;
//       let chartY = 45;
      
//       if (chartHeight > pageHeight - chartY - 20) {
//         const maxHeight = pageHeight - chartY - 20;
//         const scaledWidth = (chartCanvas.width * maxHeight) / chartCanvas.height;
//         const chartX = (pageWidth - scaledWidth) / 2;
//         doc.addImage(chartImage, "PNG", chartX, chartY, scaledWidth, maxHeight);
//       } else {
//         const chartX = (pageWidth - chartWidth) / 2;
//         doc.addImage(chartImage, "PNG", chartX, chartY, chartWidth, chartHeight);
//       }
      
//       setExportProgress(80);
      
//       // Page 3: Detailed Table
//       doc.addPage();
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.detailedData, 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
      
//       // Prepare table data
//       const tableData = processedData.map((item) => [
//         item.location,
//         item.rosterType === "A" ? "Roster" : "Common",
//         item.totalEmp.toString(),
//         item.todayAtt.toString(),
//         item.absent.toString(),
//         `${item.percentage}%`,
//       ]);
      
//       // Add total row
//       tableData.push([
//         t.total,
//         `${totalStats.rosterCount}R/${totalStats.commonCount}C`,
//         totalStats.total.toString(),
//         totalStats.present.toString(),
//         totalStats.absent.toString(),
//         `${totalStats.avgPercentage}%`,
//       ]);
      
//       setExportProgress(90);
      
//       // Generate table
//       autoTable(doc, {
//         startY: 35,
//         head: [[t.location, "Type", t.strength, t.todayAttendance, t.absent, t.attendanceRate]],
//         body: tableData,
//         styles: {
//           fontSize: 9,
//           cellPadding: 4,
//         },
//         headStyles: {
//           fillColor: [21, 76, 121],
//           textColor: 255,
//           fontStyle: "bold",
//         },
//         alternateRowStyles: {
//           fillColor: [245, 248, 252],
//         },
//         didDrawCell: (data) => {
//           // Highlight total row
//           if (data.row.index === tableData.length - 1) {
//             doc.setFillColor(21, 76, 121);
//             doc.setTextColor(255, 255, 255);
//             doc.setFont("helvetica", "bold");
//           }
//         },
//       });
      
//       // Footer on all pages
//       const pageCount = doc.internal.getNumberOfPages();
//       for (let i = 1; i <= pageCount; i++) {
//         doc.setPage(i);
//         doc.setFontSize(10);
//         doc.setTextColor(128, 128, 128);
//         doc.text(
//           `${t.page} ${i} ${t.of} ${pageCount}`,
//           pageWidth - 20,
//           pageHeight - 10,
//           { align: "right" }
//         );
//         doc.text(
//           `${t.generatedOn} ${new Date().toLocaleDateString()}`,
//           20,
//           pageHeight - 10
//         );
//       }

//       setExportProgress(100);
      
//       // Save the PDF
//       const filename = `Roster_Attendance_Report_${formatDate(selectedDate)}.pdf`;
//       doc.save(filename);
      
//       // Show success message
//       showSnackbar(t.pdfSuccess, "success");
      
//       // Reset progress after a delay
//       setTimeout(() => {
//         setExporting(false);
//         setExportProgress(0);
//       }, 1000);
      
//     } catch (error) {
//       console.error("Error generating PDF:", error);
//       showSnackbar(t.pdfError, "error");
//       setExporting(false);
//       setExportProgress(0);
//     }
//   };

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         {/* Export Progress Backdrop */}
//         <Backdrop
//           sx={{
//             color: "#fff",
//             zIndex: (theme) => theme.zIndex.drawer + 1,
//             backgroundColor: "rgba(0, 0, 0, 0.7)",
//           }}
//           open={exporting}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//               gap: 2,
//               p: 4,
//               bgcolor: "background.paper",
//               borderRadius: 2,
//               boxShadow: 24,
//               maxWidth: 400,
//               width: "100%",
//             }}
//           >
//             <CircularProgress
//               size={60}
//               thickness={4}
//               sx={{ color: "#154c79" }}
//             />
//             <Typography variant="h6" color="primary" fontWeight="bold">
//               {exporting ? t.exporting : t.exportPdf}
//             </Typography>
//             <Box sx={{ width: "100%", mt: 2 }}>
//               <LinearProgress
//                 variant="determinate"
//                 value={exportProgress}
//                 sx={{
//                   height: 10,
//                   borderRadius: 5,
//                   backgroundColor: "#e0e0e0",
//                   "& .MuiLinearProgress-bar": {
//                     backgroundColor: "#2e7d32",
//                   },
//                 }}
//               />
//               <Typography
//                 variant="body2"
//                 color="text.secondary"
//                 align="center"
//                 mt={1}
//               >
//                 {exportProgress}% Complete
//               </Typography>
//             </Box>
//             <Typography variant="body2" color="text.secondary" align="center">
//               {t.prepareReport}
//             </Typography>
//           </Box>
//         </Backdrop>

//         {/* Success/Error Snackbar */}
//         <Snackbar
//           open={snackbar.open}
//           autoHideDuration={4000}
//           onClose={closeSnackbar}
//           anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//         >
//           <Alert
//             onClose={closeSnackbar}
//             severity={snackbar.severity}
//             sx={{ width: "100%" }}
//             elevation={6}
//             variant="filled"
//           >
//             {snackbar.message}
//           </Alert>
//         </Snackbar>

//         {/* Header Section */}
//         <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
//           <Typography
//             sx={{
//               fontSize: "16px",
//               fontWeight: 700,
//               color: "#0e3c66",
//               textAlign: "center",
//               flex: 1,
//             }}
//           >
//             {t.title}
//           </Typography>
          
//           {/* Language Selector */}
//           <Tooltip title={t.language}>
//             <IconButton
//               onClick={handleLanguageMenuOpen}
//               sx={{
//                 backgroundColor: language === 'si' ? '#142c54' : '#f0f0f0',
//                 color: language === 'si' ? 'white' : '#142c54',
//                 '&:hover': {
//                   backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
//                 },
//                 width: '36px',
//                 height: '36px',
//                 ml: 1,
//               }}
//             >
//               {language === 'si' ? (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
//               ) : (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
//               )}
//             </IconButton>
//           </Tooltip>
          
//           <Menu
//             anchorEl={anchorEl}
//             open={Boolean(anchorEl)}
//             onClose={handleLanguageMenuClose}
//             PaperProps={{
//               sx: {
//                 mt: 1,
//                 minWidth: 120,
//                 borderRadius: '8px',
//                 boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
//               }
//             }}
//           >
//             <MenuItem 
//               onClick={() => handleLanguageChange('en')}
//               selected={language === 'en'}
//               sx={{
//                 backgroundColor: language === 'en' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <LanguageIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
//                   English
//                 </Typography>
//               </Box>
//             </MenuItem>
//             <MenuItem 
//               onClick={() => handleLanguageChange('si')}
//               selected={language === 'si'}
//               sx={{
//                 backgroundColor: language === 'si' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <TranslateIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
//                   සිංහල
//                 </Typography>
//               </Box>
//             </MenuItem>
//           </Menu>
//         </Box>

//         {/* Date Picker and Export Button */}
//         <Box
//           sx={{
//             display: "flex",
//             gap: 1,
//             mb: 1.5,
//             alignItems: "center",
//             flexWrap: isMobile ? "wrap" : "nowrap",
//           }}
//         >
//           <Button
//             variant="contained"
//             startIcon={<CalendarTodayIcon />}
//             onClick={() => setOpenDatePicker(true)}
//             disabled={exporting}
//             sx={{
//               flex: isMobile ? 1 : 3,
//               minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//               height: "38px",
//               borderRadius: "8px",
//               backgroundColor: exporting ? "#9e9e9e" : "#142c54",
//               color: "#fff",
//               fontWeight: "bold",
//               textTransform: "none",
//               fontSize: "13px",
//               "&:hover": { 
//                 backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
//               },
//             }}
//           >
//             {formatDate(selectedDate)}
//           </Button>

//           {!loading && processedData.length > 0 && (
//             <Button
//               variant="contained"
//               startIcon={<PictureAsPdfIcon />}
//               onClick={handleExportPDF}
//               disabled={exporting || chartData.length === 0}
//               sx={{
//                 flex: 1,
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//                 height: "38px",
//                 borderRadius: "8px",
//                 backgroundColor: exporting 
//                   ? "#9e9e9e" 
//                   : (chartData.length === 0 ? "#bdbdbd" : "#2e7d32"),
//                 color: "#fff",
//                 fontWeight: "bold",
//                 textTransform: "none",
//                 fontSize: "13px",
//                 "&:hover": { 
//                   backgroundColor: exporting 
//                     ? "#9e9e9e" 
//                     : (chartData.length === 0 ? "#bdbdbd" : "#1b5e20") 
//                 },
//               }}
//             >
//               {exporting ? t.exporting : t.exportPdf}
//             </Button>
//           )}
//         </Box>

//         {openDatePicker && (
//           <DatePicker
//             open={openDatePicker}
//             value={selectedDate}
//             onChange={handleDateChange}
//             onClose={() => setOpenDatePicker(false)}
//             renderInput={(params) => (
//               <TextField {...params} sx={{ display: "none" }} />
//             )}
//           />
//         )}

//         {/* Sticky Cards Section - Updated with 3 cards only */}
//         {!loading && processedData.length > 0 && (
//           <StickyCardsContainer>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: "6px",
//                 mb: 1,
//               }}
//             >
//               {/* Total Strength Card - Unchanged */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                   border: "1px solid #aec4e0",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0E3C66",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.totalStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {overallTotalStrength}
//                 </Typography>
//               </Box>

//               {/* Today Scheduled Attendance Strength Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
//                   border: "1px solid #ffb74d",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#e65100",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.scheduledStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {totalStats.scheduledStrength}
//                 </Typography>
//               </Box>

//               {/* Today Strength Card - COMMENTED OUT */}
//               {/* 
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
//                   border: "1px solid #90caf9",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0d47a1",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.todayStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {totalStats.total}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500 }}>
//                   {totalStats.rosterCount + totalStats.commonCount} Locations
//                 </Typography>
//               </Box>
//               */}

//               {/* Today Attendance Card - Renamed from Present */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//                   border: "1px solid #80cbc4",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#004d40",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.todayAttendance}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {totalStats.present}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color:
//                       totalStats.avgPercentage > 70
//                         ? "#2E7D32"
//                         : totalStats.avgPercentage > 50
//                         ? "#FF9800"
//                         : "#D32F2F",
//                   }}
//                 >
//                   ({totalStats.avgPercentage}%)
//                 </Typography>
//               </Box>
//             </Box>
//           </StickyCardsContainer>
//         )}

//         {/* Content Container */}
//         <Box
//           sx={{
//             [theme.breakpoints.down("sm")]: {
//               maxHeight: "calc(100vh - 260px)",
//               overflowY: "auto",
//             },
//           }}
//         >
//           {/* Chart Section */}
//           {chartData.length > 0 && (
//             <ChartContainer ref={chartContainerRef}>
//               <ChartTitle>
//                 {t.chartTitle} ({chartData.length} {t.locationsWithAttendance})
//                 <Typography 
//                   variant="caption" 
//                   sx={{ 
//                     ml: 1, 
//                     fontSize: '11px',
//                     color: '#666'
//                   }}
//                 >
//                   {totalStats.rosterCount} {t.roster} • {totalStats.commonCount} {t.common}
//                 </Typography>
//               </ChartTitle>

//               <Box sx={{ width: "100%", height: isMobile ? 450 : 420 }}>
//                 <ResponsiveContainer width="100%" height="100%">
//                   <BarChart
//                     data={chartData}
//                     layout="vertical"
//                     barSize={isMobile ? 24 : 28}
//                     margin={{
//                       top: 5,
//                       right: isMobile ? 15 : 180,
//                       left: 0,
//                       bottom: 5,
//                     }}
//                   >
//                     <CartesianGrid
//                       strokeDasharray="2 2"
//                       stroke="#e0e0e0"
//                       horizontal={true}
//                       vertical={false}
//                     />
                    
//                     <YAxis
//                       dataKey="location"
//                       type="category"
//                       axisLine={false}
//                       tickLine={false}
//                       tick={(props) => renderColoredLocationTick({ ...props, data: chartData })}
//                       width={isMobile ? 170 : 180}
//                       interval={0}
//                     />

//                     <XAxis
//                       type="number"
//                       tick={{ fontSize: isMobile ? 9 : 10 }}
//                       axisLine={false}
//                       tickLine={false}
//                       domain={[0, 100]}
//                       ticks={[0, 20, 40, 60, 80, 100]}
//                       tickFormatter={(value) => `${value}%`}
//                     />

//                     <RechartsTooltip
//                       content={<CustomTooltip language={language} />}
//                       cursor={{ fill: "rgba(0,0,0,0.03)" }}
//                     />

//                     <Bar
//                       dataKey="percentage"
//                       name="Attendance Percentage"
//                       fill="#4CAF50"
//                       radius={[0, 10, 10, 0]}
//                     >
//                       {chartData.map((entry, index) => (
//                         <Cell
//                           key={`attendance-cell-${index}`}
//                           fill={
//                             entry.percentage > 70
//                               ? "#4CAF50"
//                               : entry.percentage > 50
//                               ? "#FF9800"
//                               : "#F44336"
//                           }
//                         />
//                       ))}

//                       <LabelList
//                         dataKey="percentage"
//                         position="insideLeft"
//                         formatter={(value) => `${value}%`}
//                         style={{
//                           fontSize: isMobile ? 9 : 10,
//                           fontWeight: 700,
//                           fill: "#fff",
//                         }}
//                         offset={12}
//                       />

//                       <LabelList
//                         dataKey="real"
//                         position="right"
//                         formatter={(value, entry) => {
//                           if (!entry || entry.total === undefined) {
//                             return `${value}`;
//                           }
//                           return `${value}/${entry.total}`;
//                         }}
//                         style={{
//                           fontSize: isMobile ? 9 : 10,
//                           fontWeight: 600,
//                           fill: "#555",
//                         }}
//                         offset={8}
//                       />
//                     </Bar>
//                   </BarChart>
//                 </ResponsiveContainer>
//               </Box>

//               {/* Color Legend - Only for attendance levels, not roster type */}
//               <Box
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   gap: "15px",
//                   mt: 1,
//                   flexWrap: "wrap",
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#4CAF50",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     {t.highAttendance} ({'>'}70%)
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#FF9800",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     {t.mediumAttendance} (50-70%)
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#F44336",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     {t.lowAttendance} ({'<'}50%)
//                   </Typography>
//                 </Box>
//               </Box>

//               {/* Roster Type Legend - Small indicator below */}
//               <Box
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   gap: "15px",
//                   mt: 0.5,
//                   flexWrap: "wrap",
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Typography sx={{ fontSize: "9px", color: "#1976d2", fontWeight: 600 }}>
//                     ● {t.roster}
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Typography sx={{ fontSize: "9px", color: "#000000", fontWeight: 600 }}>
//                     ● {t.common}
//                   </Typography>
//                 </Box>
//               </Box>
//             </ChartContainer>
//           )}

//           {/* TABLE SECTION */}
//           <StyledTableContainer component={Paper}>
//             <Table stickyHeader>
//               <TableHead>
//                 <TableRow>
//                   <StyledTableCell>{t.location}</StyledTableCell>
//                   <StyledTableCell>Type</StyledTableCell>
//                   <StyledTableCell>{t.strength}</StyledTableCell>
//                   <StyledTableCell>{t.todayAttendance}</StyledTableCell>
//                   <StyledTableCell>{t.absent}</StyledTableCell>
//                   <StyledTableCell>{t.attendanceRate}</StyledTableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {!loading && processedData.length > 0 ? (
//                   <>
//                     {processedData.map((row, index) => (
//                       <StyledTableRow key={index}>
//                         <StyledTableCell
//                           sx={{
//                             textAlign: "left",
//                             pl: isMobile ? 2 : 3,
//                             maxWidth: "200px",
//                             overflow: "hidden",
//                             textOverflow: "ellipsis",
//                             whiteSpace: "nowrap",
//                             color: row.rosterType !== "N" ? "#1976d2" : "#000000",
//                             fontWeight: 600,
//                           }}
//                         >
//                           {row.location}
//                         </StyledTableCell>
//                         <StyledTableCell>
//                           <Chip
//                             label={row.rosterType !== "N" ? t.roster : t.common}
//                             size="small"
//                             sx={{
//                               backgroundColor: row.rosterType !== "N" ? "rgba(25, 118, 210, 0.1)" : "rgba(237, 108, 2, 0.1)",
//                               color: row.rosterType !== "N" ? "#1976d2" : "#ed6c02",
//                               fontWeight: 600,
//                               fontSize: "11px",
//                             }}
//                           />
//                         </StyledTableCell>
//                         <StyledTableCell>{row.totalEmp}</StyledTableCell>
//                         <StyledTableCell sx={{ color: "#2E7D32", fontWeight: 600 }}>
//                           {row.todayAtt}
//                         </StyledTableCell>
//                         <StyledTableCell sx={{ color: "#D32F2F", fontWeight: 600 }}>
//                           {row.absent}
//                         </StyledTableCell>
//                         <StyledTableCell>
//                           <Box
//                             sx={{
//                               display: "inline-flex",
//                               alignItems: "center",
//                               gap: 0.5,
//                             }}
//                           >
//                             <span>{row.percentage}%</span>
//                             <Box
//                               sx={{
//                                 width: "6px",
//                                 height: "6px",
//                                 borderRadius: "50%",
//                                 backgroundColor:
//                                   row.percentage > 70
//                                     ? "#4CAF50"
//                                     : row.percentage > 50
//                                     ? "#FF9800"
//                                     : "#F44336",
//                               }}
//                             />
//                           </Box>
//                         </StyledTableCell>
//                       </StyledTableRow>
//                     ))}
//                     <StyledTableRow
//                       sx={{
//                         backgroundColor: "#154c79 !important",
//                         "&:hover": { backgroundColor: "#154c79 !important" },
//                       }}
//                     >
//                       <StyledTableCell
//                         sx={{
//                           color: "#ffffff",
//                           fontWeight: 700,
//                           textAlign: "left",
//                           pl: isMobile ? 2 : 3,
//                         }}
//                       >
//                         {t.total}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStats.rosterCount}R/{totalStats.commonCount}C
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStats.total}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStats.present}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStats.absent}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         <Box
//                           sx={{
//                             display: "inline-flex",
//                             alignItems: "center",
//                             gap: 0.5,
//                           }}
//                         >
//                           <span>{totalStats.avgPercentage}%</span>
//                           <Box
//                             sx={{
//                               width: "6px",
//                               height: "6px",
//                               borderRadius: "50%",
//                               backgroundColor:
//                                 totalStats.avgPercentage > 70
//                                   ? "#4CAF50"
//                                   : totalStats.avgPercentage > 50
//                                   ? "#FF9800"
//                                   : "#F44336",
//                             }}
//                           />
//                         </Box>
//                       </StyledTableCell>
//                     </StyledTableRow>
//                   </>
//                 ) : loading ? (
//                   <TableRow>
//                     <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
//                       <Typography
//                         sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
//                       >
//                         {t.loading}
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 ) : (
//                   <TableRow>
//                     <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
//                       <Typography
//                         sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
//                       >
//                         {t.noData}
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 )}
//               </TableBody>
//             </Table>
//           </StyledTableContainer>
//         </Box>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// };

// export default RosterAttendance;



















// import React, { useEffect, useState, useMemo, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchRosterAttendance } from "../../actions/RosterAttendanceAction";
// import { fetchLocationBased } from "../../actions/LocationBasedAction";
// import {
//   Box,
//   CircularProgress,
//   Typography,
//   Paper,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   useTheme,
//   useMediaQuery,
//   Button,
//   Snackbar,
//   Alert,
//   LinearProgress,
//   Backdrop,
//   IconButton,
//   Menu,
//   MenuItem,
//   Tooltip,
//   TextField,
//   Chip,
// } from "@mui/material";
// import { format } from "date-fns";
// import { useLocation } from "react-router-dom";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   ResponsiveContainer,
//   LabelList,
//   Cell,
//   Tooltip as RechartsTooltip,
// } from "recharts";
// import { styled } from "@mui/material/styles";
// import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import TranslateIcon from "@mui/icons-material/Translate";
// import LanguageIcon from "@mui/icons-material/Language";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import html2canvas from "html2canvas";

// // ------------ Translation Texts ------------
// const translations = {
//   en: {
//     title: "Roster Attendance",
//     datePicker: "Select Date",
//     exportPdf: "Export Full PDF",
//     exporting: "Exporting...",
//     totalStrength: "Total Strength",
//     rosterStrength: "Roster Strength",
//     present: "Present",
//     absent: "Absent",
//     location: "Location",
//     strength: "Strength",
//     attendanceRate: "Attendance Rate (%)",
//     chartTitle: "Roster Attendance by Location",
//     locationsWithAttendance: "locations with attendance",
//     highAttendance: "High Attendance",
//     mediumAttendance: "Medium",
//     lowAttendance: "Low",
//     loading: "Loading roster data...",
//     noData: "No roster attendance data available for the selected date.",
//     total: "Total",
//     reportTitle: "Roster Attendance Report",
//     reportDate: "Report Date",
//     summaryStats: "Summary Statistics",
//     chartViz: "Roster Attendance Visualization by Location",
//     detailedData: "Detailed Roster Attendance Data",
//     page: "Page",
//     of: "of",
//     generatedOn: "Generated on",
//     pdfSuccess: "PDF exported successfully!",
//     pdfError: "Failed to export PDF. Please try again.",
//     prepareReport: "Preparing your report with chart and table data...",
//     language: "Language",
//     roster: "Roster",
//     common: "Common",
//     rosterLocations: "Roster Locations",
//     commonLocations: "Common Locations",
//   },
//   si: {
//     title: "රෝස්ටර් පැමිණීම්",
//     datePicker: "දිනය තෝරන්න",
//     exportPdf: "PDF බාගත කරන්න",
//     exporting: "PDF බාගත කරමින්...",
//     totalStrength: "මුළු සේවක පිරිස",
//     rosterStrength: "රෝස්ටර් සේවක පිරිස",
//     present: "පැමිණි සේවකයන්",
//     absent: "පැමිණ නොමැති සේවකයන්",
//     location: "ස්ථානය",
//     strength: "සේවක පිරිස",
//     attendanceRate: "පැමිණීම් අනුපාතය (%)",
//     chartTitle: "ස්ථානය අනුව රෝස්ටර් පැමිණීම්",
//     locationsWithAttendance: "පැමිණීම් සහිත ස්ථාන",
//     highAttendance: "ඉහළ පැමිණීම් අනුපාතය",
//     mediumAttendance: "මධ්‍යම පැමිණීම් අනුපාතය",
//     lowAttendance: "අඩු පැමිණීම් අනුපාතය",
//     loading: "රෝස්ටර් දත්ත ලබා ගනිමින්...",
//     noData: "තෝරාගත් දිනය සඳහා රෝස්ටර් පැමිණීම් දත්ත නොමැත.",
//     total: "සියල්ල",
//     reportTitle: "රෝස්ටර් පැමිණීම් වාර්තාව",
//     reportDate: "වාර්තා දිනය",
//     summaryStats: "සාරාංශ සංඛ්‍යාලේඛන",
//     chartViz: "ස්ථානය අනුව රෝස්ටර් පැමිණීම් දෘශ්‍යකරණය",
//     detailedData: "සවිස්තරාත්මක රෝස්ටර් පැමිණීම් දත්ත",
//     page: "පිටුව",
//     of: "යි",
//     generatedOn: "සෑදූ දිනය",
//     pdfSuccess: "PDF ගොනුව සාර්ථකව බාගත කරන ලදී!",
//     pdfError: "PDF ගොනුව බාගත කිරීම අසාර්ථක විය. කරුණාකර නැවත උත්සාහ කරන්න.",
//     prepareReport: "ප්‍රස්ථාරය සහ වගු දත්ත සමඟ ඔබේ වාර්තාව සකස් කරමින්...",
//     language: "භාෂාව",
//     roster: "රෝස්ටර්",
//     common: "පොදු",
//     rosterLocations: "රෝස්ටර් ස්ථාන",
//     commonLocations: "පොදු ස්ථාන",
//   }
// };

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "1000px",
//   margin: "0 auto",
//   padding: "8px 12px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     padding: "0px 8px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "10px",
//   boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
//   border: "1px solid #d1d9e6",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "5px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "2px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   [theme.breakpoints.up("sm")]: {
//     maxHeight: "380px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "none",
//     overflowY: "visible",
//     overflowX: "auto",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "8px 6px",
//   fontSize: "13px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.3px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "10px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "10px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "6px 4px",
//     fontSize: "11px",
//     "&.MuiTableCell-head": {
//       fontSize: "12px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.15s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "10px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "10px",
//   },
// }));

// const ChartContainer = styled(Box)(({ theme }) => ({
//   position: "relative",
//   backgroundColor: "#f9fafc",
//   borderRadius: "10px",
//   padding: theme.spacing(1.5),
//   boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
//   border: "1px solid #e6e9ef",
//   marginBottom: theme.spacing(1.5),
//   [theme.breakpoints.down("sm")]: {
//     padding: theme.spacing(1),
//   },
// }));

// const ChartTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "14px",
//   fontWeight: 700,
//   color: "#0e3c66",
//   marginBottom: theme.spacing(1),
//   textAlign: "center",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "12px",
//   },
// }));

// const CustomTooltip = ({ active, payload, language }) => {
//   if (active && payload && payload.length) {
//     const data = payload[0].payload;
//     const t = translations[language];
    
//     return (
//       <Paper
//         sx={{
//           padding: "8px 10px",
//           backgroundColor: "rgba(255, 255, 255, 0.98)",
//           border: "1px solid #154c79",
//           borderRadius: "6px",
//           boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
//           minWidth: "150px",
//         }}
//       >
//         <Box sx={{ mb: 0.5 }}>
//           <Typography
//             sx={{
//               fontSize: "12px",
//               fontWeight: 800,
//               color: "#154c79",
//               textTransform: "uppercase",
//             }}
//           >
//             {data.location}
//           </Typography>
//           <Typography
//             sx={{
//               fontSize: "9px",
//               fontWeight: 600,
//               color: data.rosterType === "A" ? "#1976d2" : "#000000",
//               mt: 0.2,
//             }}
//           >
//             {data.rosterType === "A" ? "Roster Location" : "Common Location"}
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.strength}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0e3c66" }}>
//               {data.total}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.present}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32" }}>
//               {data.real}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.absent}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#F44336" }}>
//               {data.absent}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.attendanceRate}:
//             </Typography>
//             <Typography 
//               sx={{ 
//                 fontSize: "11px", 
//                 fontWeight: 700, 
//                 color: data.percentage > 70 ? "#2E7D32" : data.percentage > 50 ? "#FF9800" : "#F44336"
//               }}
//             >
//               {data.percentage}%
//             </Typography>
//           </Box>
//         </Box>
//       </Paper>
//     );
//   }
//   return null;
// };

// const RosterAttendance = () => {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { loading, data: rosterData } = useSelector((state) => state.rosterAttendance);
//   const { data: locationData } = useSelector((state) => state.locationBased);
//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [exportProgress, setExportProgress] = useState(0);
//   const [exporting, setExporting] = useState(false);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });
//   const [language, setLanguage] = useState('en');
//   const [anchorEl, setAnchorEl] = useState(null);
//   const location = useLocation();
  
//   const chartContainerRef = useRef(null);
//   const t = translations[language];

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     dispatch(fetchRosterAttendance(formatDate(selectedDate)));
//     dispatch(fetchLocationBased(formatDate(selectedDate)));
//   }, [dispatch, selectedDate]);

//   const handleDateChange = (date) => {
//     setSelectedDate(date);
//     setOpenDatePicker(false);
//     const formatted = formatDate(date);
//     dispatch(fetchRosterAttendance(formatted));
//     dispatch(fetchLocationBased(formatted));
//   };

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbar({
//       open: true,
//       message,
//       severity,
//     });
//   };

//   const closeSnackbar = () => {
//     setSnackbar({ ...snackbar, open: false });
//   };

//   const handleLanguageMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget);
//   };

//   const handleLanguageMenuClose = () => {
//     setAnchorEl(null);
//   };

//   const handleLanguageChange = (lang) => {
//     setLanguage(lang);
//     handleLanguageMenuClose();
//   };

//   const processedData = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) return [];

//     return rosterData
//       .filter((item) => Number(item.TodayAtt || 0) > 0)
//       .map((item) => ({
//         location: item.location,
//         totalEmp: Number(item.TotalEmp || 0),
//         todayAtt: Number(item.TodayAtt || 0),
//         absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//         percentage: Math.round(Number(item.Percentage || 0)),
//         rosterType: item.Roster || "N",
//       }));
//   }, [rosterData]);

//   const chartData = useMemo(() => {
//     return processedData.map((item) => ({
//       location: item.location,
//       total: item.totalEmp,
//       real: item.todayAtt,
//       absent: item.absent,
//       percentage: item.percentage,
//       rosterType: item.rosterType,
//       barSize: item.percentage,
//     }));
//   }, [processedData]);

//   const totalStats = useMemo(() => {
//     const total = processedData.reduce((sum, item) => sum + item.totalEmp, 0);
//     const present = processedData.reduce((sum, item) => sum + item.todayAtt, 0);
//     const absent = total - present;
//     const avgPercentage = total > 0 ? Math.round((present / total) * 100) : 0;

//     const rosterItems = processedData.filter(item => item.rosterType === "A");
//     const commonItems = processedData.filter(item => item.rosterType === "N");
    
//     const rosterTotal = rosterItems.reduce((sum, item) => sum + item.totalEmp, 0);
//     const rosterPresent = rosterItems.reduce((sum, item) => sum + item.todayAtt, 0);
//     const rosterPercentage = rosterTotal > 0 ? Math.round((rosterPresent / rosterTotal) * 100) : 0;
    
//     const commonTotal = commonItems.reduce((sum, item) => sum + item.totalEmp, 0);
//     const commonPresent = commonItems.reduce((sum, item) => sum + item.todayAtt, 0);
//     const commonPercentage = commonTotal > 0 ? Math.round((commonPresent / commonTotal) * 100) : 0;

//     return { 
//       total, 
//       present, 
//       absent, 
//       avgPercentage,
//       rosterTotal,
//       rosterPresent,
//       rosterPercentage,
//       commonTotal,
//       commonPresent,
//       commonPercentage,
//       rosterCount: rosterItems.length,
//       commonCount: commonItems.length
//     };
//   }, [processedData]);

//   const overallTotalStrength = useMemo(() => {
//     if (!locationData || !Array.isArray(locationData)) return 0;
//     return locationData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//   }, [locationData]);

//   // Custom YAxis tick component to render colored location names
//   const renderColoredLocationTick = (props) => {
//     const { x, y, payload, data } = props;
//     const locationData = data?.find(item => item.location === payload.value);
//     const rosterType = locationData?.rosterType || "N";
    
//     return (
//       <g transform={`translate(${x},${y})`}>
//         <text
//           x={-150}
//           y={0}
//           dy={4}
//           textAnchor="start"
//           fill={rosterType === "A" ? "#1976d2" : "#000000"}
//           fontSize={isMobile ? 10 : 11}
//           fontWeight={600}
//         >
//           {payload.value}
//         </text>
//       </g>
//     );
//   };

//   // ================= PDF EXPORT FUNCTION =================
//   const handleExportPDF = async () => {
//     if (!chartContainerRef.current || exporting) return;
    
//     try {
//       setExporting(true);
//       setExportProgress(10);
      
//       // Step 1: Capture chart as image
//       setExportProgress(30);
//       const chartCanvas = await html2canvas(chartContainerRef.current, {
//         scale: 2,
//         backgroundColor: "#F9FAFC",
//         logging: false,
//         useCORS: true,
//       });
//       const chartImage = chartCanvas.toDataURL("image/png");
      
//       setExportProgress(60);
      
//       // Step 2: Create PDF document
//       const doc = new jsPDF("p", "mm", "a4");
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();
      
//       // Page 1: Cover page with title and summary
//       doc.setFontSize(20);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.reportTitle, pageWidth / 2, 40, { align: "center" });
      
//       doc.setFontSize(14);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, pageWidth / 2, 60, { align: "center" });
      
//       // Summary statistics
//       doc.setFontSize(12);
//       doc.text(t.summaryStats, pageWidth / 2, 80, { align: "center" });
      
//       const summaryY = 90;
//       doc.text(`${t.totalStrength}: ${overallTotalStrength}`, 30, summaryY);
//       doc.text(`Roster Strength: ${totalStats.rosterTotal} (${totalStats.rosterCount} locations)`, 30, summaryY + 8);
//       doc.text(`Common Strength: ${totalStats.commonTotal} (${totalStats.commonCount} locations)`, 30, summaryY + 16);
//       doc.text(`${t.present}: ${totalStats.present} (${totalStats.avgPercentage}%)`, 30, summaryY + 24);
//       doc.text(`${t.absent}: ${totalStats.absent} (${100 - totalStats.avgPercentage}%)`, 30, summaryY + 32);
      
//       // Add decorative line
//       doc.setDrawColor(21, 76, 121);
//       doc.setLineWidth(0.5);
//       doc.line(30, summaryY + 45, pageWidth - 30, summaryY + 45);
      
//       setExportProgress(70);
      
//       // Page 2: Chart Visualization
//       doc.addPage();
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.chartViz, 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
//       doc.text(`${t.chartTitle} (${chartData.length} ${t.locationsWithAttendance})`, 20, 34);
      
//       // Add chart image
//       const chartWidth = pageWidth - 40;
//       const chartHeight = (chartCanvas.height * chartWidth) / chartCanvas.width;
//       let chartY = 45;
      
//       if (chartHeight > pageHeight - chartY - 20) {
//         const maxHeight = pageHeight - chartY - 20;
//         const scaledWidth = (chartCanvas.width * maxHeight) / chartCanvas.height;
//         const chartX = (pageWidth - scaledWidth) / 2;
//         doc.addImage(chartImage, "PNG", chartX, chartY, scaledWidth, maxHeight);
//       } else {
//         const chartX = (pageWidth - chartWidth) / 2;
//         doc.addImage(chartImage, "PNG", chartX, chartY, chartWidth, chartHeight);
//       }
      
//       setExportProgress(80);
      
//       // Page 3: Detailed Table
//       doc.addPage();
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.detailedData, 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${formatDate(selectedDate)}`, 20, 28);
      
//       // Prepare table data
//       const tableData = processedData.map((item) => [
//         item.location,
//         item.rosterType === "A" ? "Roster" : "Common",
//         item.totalEmp.toString(),
//         item.todayAtt.toString(),
//         item.absent.toString(),
//         `${item.percentage}%`,
//       ]);
      
//       // Add total row
//       tableData.push([
//         t.total,
//         `${totalStats.rosterCount}R/${totalStats.commonCount}C`,
//         totalStats.total.toString(),
//         totalStats.present.toString(),
//         totalStats.absent.toString(),
//         `${totalStats.avgPercentage}%`,
//       ]);
      
//       setExportProgress(90);
      
//       // Generate table
//       autoTable(doc, {
//         startY: 35,
//         head: [[t.location, "Type", t.strength, t.present, t.absent, t.attendanceRate]],
//         body: tableData,
//         styles: {
//           fontSize: 9,
//           cellPadding: 4,
//         },
//         headStyles: {
//           fillColor: [21, 76, 121],
//           textColor: 255,
//           fontStyle: "bold",
//         },
//         alternateRowStyles: {
//           fillColor: [245, 248, 252],
//         },
//         didDrawCell: (data) => {
//           // Highlight total row
//           if (data.row.index === tableData.length - 1) {
//             doc.setFillColor(21, 76, 121);
//             doc.setTextColor(255, 255, 255);
//             doc.setFont("helvetica", "bold");
//           }
//         },
//       });
      
//       // Footer on all pages
//       const pageCount = doc.internal.getNumberOfPages();
//       for (let i = 1; i <= pageCount; i++) {
//         doc.setPage(i);
//         doc.setFontSize(10);
//         doc.setTextColor(128, 128, 128);
//         doc.text(
//           `${t.page} ${i} ${t.of} ${pageCount}`,
//           pageWidth - 20,
//           pageHeight - 10,
//           { align: "right" }
//         );
//         doc.text(
//           `${t.generatedOn} ${new Date().toLocaleDateString()}`,
//           20,
//           pageHeight - 10
//         );
//       }

//       setExportProgress(100);
      
//       // Save the PDF
//       const filename = `Roster_Attendance_Report_${formatDate(selectedDate)}.pdf`;
//       doc.save(filename);
      
//       // Show success message
//       showSnackbar(t.pdfSuccess, "success");
      
//       // Reset progress after a delay
//       setTimeout(() => {
//         setExporting(false);
//         setExportProgress(0);
//       }, 1000);
      
//     } catch (error) {
//       console.error("Error generating PDF:", error);
//       showSnackbar(t.pdfError, "error");
//       setExporting(false);
//       setExportProgress(0);
//     }
//   };

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         {/* Export Progress Backdrop */}
//         <Backdrop
//           sx={{
//             color: "#fff",
//             zIndex: (theme) => theme.zIndex.drawer + 1,
//             backgroundColor: "rgba(0, 0, 0, 0.7)",
//           }}
//           open={exporting}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//               gap: 2,
//               p: 4,
//               bgcolor: "background.paper",
//               borderRadius: 2,
//               boxShadow: 24,
//               maxWidth: 400,
//               width: "100%",
//             }}
//           >
//             <CircularProgress
//               size={60}
//               thickness={4}
//               sx={{ color: "#154c79" }}
//             />
//             <Typography variant="h6" color="primary" fontWeight="bold">
//               {exporting ? t.exporting : t.exportPdf}
//             </Typography>
//             <Box sx={{ width: "100%", mt: 2 }}>
//               <LinearProgress
//                 variant="determinate"
//                 value={exportProgress}
//                 sx={{
//                   height: 10,
//                   borderRadius: 5,
//                   backgroundColor: "#e0e0e0",
//                   "& .MuiLinearProgress-bar": {
//                     backgroundColor: "#2e7d32",
//                   },
//                 }}
//               />
//               <Typography
//                 variant="body2"
//                 color="text.secondary"
//                 align="center"
//                 mt={1}
//               >
//                 {exportProgress}% Complete
//               </Typography>
//             </Box>
//             <Typography variant="body2" color="text.secondary" align="center">
//               {t.prepareReport}
//             </Typography>
//           </Box>
//         </Backdrop>

//         {/* Success/Error Snackbar */}
//         <Snackbar
//           open={snackbar.open}
//           autoHideDuration={4000}
//           onClose={closeSnackbar}
//           anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//         >
//           <Alert
//             onClose={closeSnackbar}
//             severity={snackbar.severity}
//             sx={{ width: "100%" }}
//             elevation={6}
//             variant="filled"
//           >
//             {snackbar.message}
//           </Alert>
//         </Snackbar>

//         {/* Header Section */}
//         <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
//           <Typography
//             sx={{
//               fontSize: "16px",
//               fontWeight: 700,
//               color: "#0e3c66",
//               textAlign: "center",
//               flex: 1,
//             }}
//           >
//             {t.title}
//           </Typography>
          
//           {/* Language Selector */}
//           <Tooltip title={t.language}>
//             <IconButton
//               onClick={handleLanguageMenuOpen}
//               sx={{
//                 backgroundColor: language === 'si' ? '#142c54' : '#f0f0f0',
//                 color: language === 'si' ? 'white' : '#142c54',
//                 '&:hover': {
//                   backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
//                 },
//                 width: '36px',
//                 height: '36px',
//                 ml: 1,
//               }}
//             >
//               {language === 'si' ? (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
//               ) : (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
//               )}
//             </IconButton>
//           </Tooltip>
          
//           <Menu
//             anchorEl={anchorEl}
//             open={Boolean(anchorEl)}
//             onClose={handleLanguageMenuClose}
//             PaperProps={{
//               sx: {
//                 mt: 1,
//                 minWidth: 120,
//                 borderRadius: '8px',
//                 boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
//               }
//             }}
//           >
//             <MenuItem 
//               onClick={() => handleLanguageChange('en')}
//               selected={language === 'en'}
//               sx={{
//                 backgroundColor: language === 'en' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <LanguageIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
//                   English
//                 </Typography>
//               </Box>
//             </MenuItem>
//             <MenuItem 
//               onClick={() => handleLanguageChange('si')}
//               selected={language === 'si'}
//               sx={{
//                 backgroundColor: language === 'si' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <TranslateIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
//                   සිංහල
//                 </Typography>
//               </Box>
//             </MenuItem>
//           </Menu>
//         </Box>

//         {/* Date Picker and Export Button */}
//         <Box
//           sx={{
//             display: "flex",
//             gap: 1,
//             mb: 1.5,
//             alignItems: "center",
//             flexWrap: isMobile ? "wrap" : "nowrap",
//           }}
//         >
//           <Button
//             variant="contained"
//             startIcon={<CalendarTodayIcon />}
//             onClick={() => setOpenDatePicker(true)}
//             disabled={exporting}
//             sx={{
//               flex: isMobile ? 1 : 3,
//               minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//               height: "38px",
//               borderRadius: "8px",
//               backgroundColor: exporting ? "#9e9e9e" : "#142c54",
//               color: "#fff",
//               fontWeight: "bold",
//               textTransform: "none",
//               fontSize: "13px",
//               "&:hover": { 
//                 backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
//               },
//             }}
//           >
//             {formatDate(selectedDate)}
//           </Button>

//           {!loading && processedData.length > 0 && (
//             <Button
//               variant="contained"
//               startIcon={<PictureAsPdfIcon />}
//               onClick={handleExportPDF}
//               disabled={exporting || chartData.length === 0}
//               sx={{
//                 flex: 1,
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//                 height: "38px",
//                 borderRadius: "8px",
//                 backgroundColor: exporting 
//                   ? "#9e9e9e" 
//                   : (chartData.length === 0 ? "#bdbdbd" : "#2e7d32"),
//                 color: "#fff",
//                 fontWeight: "bold",
//                 textTransform: "none",
//                 fontSize: "13px",
//                 "&:hover": { 
//                   backgroundColor: exporting 
//                     ? "#9e9e9e" 
//                     : (chartData.length === 0 ? "#bdbdbd" : "#1b5e20") 
//                 },
//               }}
//             >
//               {exporting ? t.exporting : t.exportPdf}
//             </Button>
//           )}
//         </Box>

//         {openDatePicker && (
//           <DatePicker
//             open={openDatePicker}
//             value={selectedDate}
//             onChange={handleDateChange}
//             onClose={() => setOpenDatePicker(false)}
//             renderInput={(params) => (
//               <TextField {...params} sx={{ display: "none" }} />
//             )}
//           />
//         )}

//         {/* Sticky Cards Section */}
//         {!loading && processedData.length > 0 && (
//           <StickyCardsContainer>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: "6px",
//                 mb: 1,
//               }}
//             >
//               {/* Total Strength Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                   border: "1px solid #aec4e0",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0E3C66",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.totalStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {overallTotalStrength}
//                 </Typography>
//               </Box>

//               {/* Roster Strength Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
//                   border: "1px solid #90caf9",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0d47a1",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.roster}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 800 }}>
//                   {totalStats.rosterCount} Locations
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "11px" : "13px", fontWeight: 700 }}>
//                   {totalStats.rosterPresent}/{totalStats.rosterTotal} ({totalStats.rosterPercentage}%)
//                 </Typography>
//               </Box>

//               {/* Present Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//                   border: "1px solid #80cbc4",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#004d40",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.present}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {totalStats.present}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color:
//                       totalStats.avgPercentage > 70
//                         ? "#2E7D32"
//                         : totalStats.avgPercentage > 50
//                         ? "#FF9800"
//                         : "#D32F2F",
//                   }}
//                 >
//                   ({totalStats.avgPercentage}%)
//                 </Typography>
//               </Box>

//               {/* Absent Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
//                   border: "1px solid #ef9a9a",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#b71c1c",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.absent}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {totalStats.absent}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color: "#D32F2F",
//                   }}
//                 >
//                   ({100 - totalStats.avgPercentage}%)
//                 </Typography>
//               </Box>

//               {/* Common Strength Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
//                   border: "1px solid #ce93d8",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#4a148c",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.common}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 800 }}>
//                   {totalStats.commonCount} Locations
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "11px" : "13px", fontWeight: 700 }}>
//                   {totalStats.commonPresent}/{totalStats.commonTotal} ({totalStats.commonPercentage}%)
//                 </Typography>
//               </Box>
//             </Box>
//           </StickyCardsContainer>
//         )}

//         {/* Content Container */}
//         <Box
//           sx={{
//             [theme.breakpoints.down("sm")]: {
//               maxHeight: "calc(100vh - 260px)",
//               overflowY: "auto",
//             },
//           }}
//         >
//           {/* Chart Section */}
//           {chartData.length > 0 && (
//             <ChartContainer ref={chartContainerRef}>
//               <ChartTitle>
//                 {t.chartTitle} ({chartData.length} {t.locationsWithAttendance})
//                 <Typography 
//                   variant="caption" 
//                   sx={{ 
//                     ml: 1, 
//                     fontSize: '11px',
//                     color: '#666'
//                   }}
//                 >
//                   {totalStats.rosterCount} {t.roster} • {totalStats.commonCount} {t.common}
//                 </Typography>
//               </ChartTitle>

//               <Box sx={{ width: "100%", height: isMobile ? 450 : 420 }}>
//                 <ResponsiveContainer width="100%" height="100%">
//                   <BarChart
//                     data={chartData}
//                     layout="vertical"
//                     barSize={isMobile ? 24 : 28}
//                     margin={{
//                       top: 5,
//                       right: isMobile ? 10 : 180,
//                       left: 0,
//                       bottom: 5,
//                     }}
//                   >
//                     <CartesianGrid
//                       strokeDasharray="2 2"
//                       stroke="#e0e0e0"
//                       horizontal={true}
//                       vertical={false}
//                     />
                    
//                     <YAxis
//                       dataKey="location"
//                       type="category"
//                       axisLine={false}
//                       tickLine={false}
//                       tick={(props) => renderColoredLocationTick({ ...props, data: chartData })}
//                       width={isMobile ? 170 : 180}
//                       interval={0}
//                     />

//                     <XAxis
//                       type="number"
//                       tick={{ fontSize: isMobile ? 9 : 10 }}
//                       axisLine={false}
//                       tickLine={false}
//                       domain={[0, 100]}
//                       ticks={[0, 20, 40, 60, 80, 100]}
//                       tickFormatter={(value) => `${value}%`}
//                     />

//                     <RechartsTooltip
//                       content={<CustomTooltip language={language} />}
//                       cursor={{ fill: "rgba(0,0,0,0.03)" }}
//                     />

//                     <Bar
//                       dataKey="percentage"
//                       name="Attendance Percentage"
//                       fill="#4CAF50"
//                       radius={[0, 10, 10, 0]}
//                     >
//                       {chartData.map((entry, index) => (
//                         <Cell
//                           key={`attendance-cell-${index}`}
//                           fill={
//                             entry.percentage > 70
//                               ? "#4CAF50"
//                               : entry.percentage > 50
//                               ? "#FF9800"
//                               : "#F44336"
//                           }
//                         />
//                       ))}

//                       <LabelList
//                         dataKey="percentage"
//                         position="insideLeft"
//                         formatter={(value) => `${value}%`}
//                         style={{
//                           fontSize: isMobile ? 9 : 10,
//                           fontWeight: 700,
//                           fill: "#fff",
//                         }}
//                         offset={12}
//                       />

//                       <LabelList
//                         dataKey="real"
//                         position="right"
//                         formatter={(value, entry) => {
//                           if (!entry || entry.total === undefined) {
//                             return `${value}`;
//                           }
//                           return `${value}/${entry.total}`;
//                         }}
//                         style={{
//                           fontSize: isMobile ? 9 : 10,
//                           fontWeight: 600,
//                           fill: "#555",
//                         }}
//                         offset={8}
//                       />
//                     </Bar>
//                   </BarChart>
//                 </ResponsiveContainer>
//               </Box>

//               {/* Color Legend - Only for attendance levels, not roster type */}
//               <Box
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   gap: "15px",
//                   mt: 1,
//                   flexWrap: "wrap",
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#4CAF50",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     {t.highAttendance} ({'>'}70%)
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#FF9800",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     {t.mediumAttendance} (50-70%)
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#F44336",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     {t.lowAttendance} ({'<'}50%)
//                   </Typography>
//                 </Box>
//               </Box>

//               {/* Roster Type Legend - Small indicator below */}
//               <Box
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   gap: "15px",
//                   mt: 0.5,
//                   flexWrap: "wrap",
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Typography sx={{ fontSize: "9px", color: "#1976d2", fontWeight: 600 }}>
//                     ● {t.roster}
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Typography sx={{ fontSize: "9px", color: "#000000", fontWeight: 600 }}>
//                     ● {t.common}
//                   </Typography>
//                 </Box>
//               </Box>
//             </ChartContainer>
//           )}

//           {/* TABLE SECTION */}
//           <StyledTableContainer component={Paper}>
//             <Table stickyHeader>
//               <TableHead>
//                 <TableRow>
//                   <StyledTableCell>{t.location}</StyledTableCell>
//                   <StyledTableCell>Type</StyledTableCell>
//                   <StyledTableCell>{t.strength}</StyledTableCell>
//                   <StyledTableCell>{t.present}</StyledTableCell>
//                   <StyledTableCell>{t.absent}</StyledTableCell>
//                   <StyledTableCell>{t.attendanceRate}</StyledTableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {!loading && processedData.length > 0 ? (
//                   <>
//                     {processedData.map((row, index) => (
//                       <StyledTableRow key={index}>
//                         <StyledTableCell
//                           sx={{
//                             textAlign: "left",
//                             pl: isMobile ? 2 : 3,
//                             maxWidth: "200px",
//                             overflow: "hidden",
//                             textOverflow: "ellipsis",
//                             whiteSpace: "nowrap",
//                             color: row.rosterType === "A" ? "#1976d2" : "#000000",
//                             fontWeight: 600,
//                           }}
//                         >
//                           {row.location}
//                         </StyledTableCell>
//                         <StyledTableCell>
//                           <Chip
//                             label={row.rosterType === "A" ? t.roster : t.common}
//                             size="small"
//                             sx={{
//                               backgroundColor: row.rosterType === "A" ? "rgba(25, 118, 210, 0.1)" : "rgba(237, 108, 2, 0.1)",
//                               color: row.rosterType === "A" ? "#1976d2" : "#ed6c02",
//                               fontWeight: 600,
//                               fontSize: "11px",
//                             }}
//                           />
//                         </StyledTableCell>
//                         <StyledTableCell>{row.totalEmp}</StyledTableCell>
//                         <StyledTableCell sx={{ color: "#2E7D32", fontWeight: 600 }}>
//                           {row.todayAtt}
//                         </StyledTableCell>
//                         <StyledTableCell sx={{ color: "#D32F2F", fontWeight: 600 }}>
//                           {row.absent}
//                         </StyledTableCell>
//                         <StyledTableCell>
//                           <Box
//                             sx={{
//                               display: "inline-flex",
//                               alignItems: "center",
//                               gap: 0.5,
//                             }}
//                           >
//                             <span>{row.percentage}%</span>
//                             <Box
//                               sx={{
//                                 width: "6px",
//                                 height: "6px",
//                                 borderRadius: "50%",
//                                 backgroundColor:
//                                   row.percentage > 70
//                                     ? "#4CAF50"
//                                     : row.percentage > 50
//                                     ? "#FF9800"
//                                     : "#F44336",
//                               }}
//                             />
//                           </Box>
//                         </StyledTableCell>
//                       </StyledTableRow>
//                     ))}
//                     <StyledTableRow
//                       sx={{
//                         backgroundColor: "#154c79 !important",
//                         "&:hover": { backgroundColor: "#154c79 !important" },
//                       }}
//                     >
//                       <StyledTableCell
//                         sx={{
//                           color: "#ffffff",
//                           fontWeight: 700,
//                           textAlign: "left",
//                           pl: isMobile ? 2 : 3,
//                         }}
//                       >
//                         {t.total}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStats.rosterCount}R/{totalStats.commonCount}C
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStats.total}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStats.present}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStats.absent}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         <Box
//                           sx={{
//                             display: "inline-flex",
//                             alignItems: "center",
//                             gap: 0.5,
//                           }}
//                         >
//                           <span>{totalStats.avgPercentage}%</span>
//                           <Box
//                             sx={{
//                               width: "6px",
//                               height: "6px",
//                               borderRadius: "50%",
//                               backgroundColor:
//                                 totalStats.avgPercentage > 70
//                                   ? "#4CAF50"
//                                   : totalStats.avgPercentage > 50
//                                   ? "#FF9800"
//                                   : "#F44336",
//                             }}
//                           />
//                         </Box>
//                       </StyledTableCell>
//                     </StyledTableRow>
//                   </>
//                 ) : loading ? (
//                   <TableRow>
//                     <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
//                       <Typography
//                         sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
//                       >
//                         {t.loading}
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 ) : (
//                   <TableRow>
//                     <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
//                       <Typography
//                         sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
//                       >
//                         {t.noData}
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 )}
//               </TableBody>
//             </Table>
//           </StyledTableContainer>
//         </Box>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// };

// export default RosterAttendance;












// import React, { useEffect, useState, useMemo } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchRosterAttendance } from "../../actions/RosterAttendanceAction";
// import { fetchLocationBased } from "../../actions/LocationBasedAction";
// import { Box, CircularProgress, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, useMediaQuery } from "@mui/material";
// import { format } from "date-fns";
// import { useLocation } from "react-router-dom";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   ResponsiveContainer,
//   LabelList,
//   Cell,
//   Tooltip as RechartsTooltip,
// } from "recharts";

// const RosterAttendance = () => {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { loading, data: rosterData } = useSelector((state) => state.rosterAttendance);
//   const { data: locationData } = useSelector((state) => state.locationBased);
//   const [selectedDate] = useState(new Date());
//   const location = useLocation();

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     dispatch(fetchRosterAttendance(formatDate(new Date())));
//     dispatch(fetchLocationBased(formatDate(new Date())));
//   }, [dispatch]);

//   const processedData = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) return [];

//     return rosterData
//       .filter((item) => Number(item.TodayAtt || 0) > 0)
//       .map((item) => ({
//         location: item.location,
//         totalEmp: Number(item.TotalEmp || 0),
//         todayAtt: Number(item.TodayAtt || 0),
//         absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//         percentage: Math.round(Number(item.Percentage || 0)),
//       }));
//   }, [rosterData]);

//   const chartData = useMemo(() => {
//     return processedData.map((item) => ({
//       location: item.location,
//       real: item.todayAtt,
//       percentage: item.percentage,
//     }));
//   }, [processedData]);

//   const totalStats = useMemo(() => {
//     const total = processedData.reduce((sum, item) => sum + item.totalEmp, 0);
//     const present = processedData.reduce((sum, item) => sum + item.todayAtt, 0);
//     const absent = total - present;
//     const avgPercentage = total > 0 ? Math.round((present / total) * 100) : 0;

//     return { total, present, absent, avgPercentage };
//   }, [processedData]);

//   const overallTotalStrength = useMemo(() => {
//     if (!locationData || !Array.isArray(locationData)) return 0;
//     return locationData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//   }, [locationData]);

//   const CustomTooltip = ({ active, payload, label }) => {
//     if (active && payload && payload.length) {
//       return (
//         <Box
//           sx={{
//             backgroundColor: "#fff",
//             border: "1px solid #ccc",
//             borderRadius: "4px",
//             padding: "8px",
//             boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
//           }}
//         >
//           <Typography sx={{ fontWeight: "bold", mb: 1 }}>{label}</Typography>
//           <Typography sx={{ color: "#4CAF50" }}>
//             Present: {payload[0].value}
//           </Typography>
//           <Typography sx={{ color: "#666" }}>
//             Attendance: {payload[0].payload.percentage}%
//           </Typography>
//         </Box>
//       );
//     }
//     return null;
//   };

//   return (
//     <Box sx={{ padding: isMobile ? "16px" : "26px", maxWidth: "1200px", margin: "0 auto" }}>
//         <Typography
//           variant="h5"
//           sx={{
//             mb: 1,
//             textAlign: "center",
//             fontWeight: "bold",
//             color: "#154C79",
//             fontFamily: "'Poppins', sans-serif",
//             fontSize: isMobile ? "16px" : "20px",
//           }}
//         >
//           {isMobile ? "Roster Attendance" : "Today Roster Attendance"} - {format(selectedDate, "yyyy-MM-dd")}
//         </Typography>

//         {/* Summary Cards */}
//         <Box sx={{ display: "flex", justifyContent: "center", gap: isMobile ? 1 : 2, mb: 3, flexWrap: "nowrap" }}>
//           <Box sx={{
//             background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
//             padding: isMobile ? "12px" : "16px",
//             borderRadius: "8px",
//             textAlign: "center",
//             minWidth: isMobile ? "100px" : "120px",
//             flex: "1 1 calc(25% - 12px)",
//             height: isMobile ? "80px" : "100px",
//             display: "flex",
//             flexDirection: "column",
//             justifyContent: "center"
//           }}>
//             <Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 500, mb: 1 }}>
//               Total Strength
//             </Typography>
//             <Typography sx={{ fontSize: isMobile ? "18px" : "24px", fontWeight: 800 }}>
//               {overallTotalStrength}
//             </Typography>
//           </Box>

//           <Box sx={{
//             background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//             padding: isMobile ? "12px" : "16px",
//             borderRadius: "8px",
//             textAlign: "center",
//             minWidth: isMobile ? "100px" : "120px",
//             flex: "1 1 calc(25% - 12px)",
//             height: isMobile ? "80px" : "100px",
//             display: "flex",
//             flexDirection: "column",
//             justifyContent: "center"
//           }}>
//             <Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 500, mb: 1 }}>
//               Total Strength (Roster)
//             </Typography>
//             <Typography sx={{ fontSize: isMobile ? "18px" : "24px", fontWeight: 800 }}>
//               {totalStats.total}
//             </Typography>
//           </Box>

//           <Box sx={{
//             background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//             padding: isMobile ? "12px" : "16px",
//             borderRadius: "8px",
//             textAlign: "center",
//             minWidth: isMobile ? "100px" : "120px",
//             flex: "1 1 calc(25% - 12px)",
//             height: isMobile ? "80px" : "100px",
//             display: "flex",
//             flexDirection: "column",
//             justifyContent: "center"
//           }}>
//             <Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 500, mb: 1 }}>
//               Present
//             </Typography>
//             <Typography sx={{ fontSize: isMobile ? "18px" : "24px", fontWeight: 800 }}>
//               {totalStats.present}
//             </Typography>
//             <Typography sx={{ fontSize: isMobile ? "10px" : "12px", fontWeight: 600, color: "#004d40" }}>
//               ({totalStats.avgPercentage}%)
//             </Typography>
//           </Box>

//           <Box sx={{
//             background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
//             padding: isMobile ? "12px" : "16px",
//             borderRadius: "8px",
//             textAlign: "center",
//             minWidth: isMobile ? "100px" : "120px",
//             flex: "1 1 calc(25% - 12px)",
//             height: isMobile ? "80px" : "100px",
//             display: "flex",
//             flexDirection: "column",
//             justifyContent: "center"
//           }}>
//             <Typography sx={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 500, mb: 1 }}>
//               Absent
//             </Typography>
//             <Typography sx={{ fontSize: isMobile ? "18px" : "24px", fontWeight: 800 }}>
//               {totalStats.absent}
//             </Typography>
//             <Typography sx={{ fontSize: isMobile ? "10px" : "12px", fontWeight: 600, color: "#b71c1c" }}>
//               ({100 - totalStats.avgPercentage}%)
//             </Typography>
//           </Box>
//         </Box>

//         {/* Chart Section */}
//         {chartData.length > 0 && (
//           <Box sx={{ mt: 3, mb: 2 }}>
//             <Typography
//               variant="h6"
//               sx={{
//                 mb: 2,
//                 textAlign: "center",
//                 fontWeight: "bold",
//                 color: "#154C79",
//                 fontSize: isMobile ? "14px" : "16px",
//               }}
//             >
//               Attendance Chart ({chartData.length} locations)
//             </Typography>

//             <Box sx={{ width: "100%", height: isMobile ? 300 : 400 }}>
//               <ResponsiveContainer width="100%" height="100%">
//                 <BarChart
//                   data={chartData}
//                   layout="vertical"
//                   barSize={28}
//                   margin={{
//                     top: 5,
//                     right: isMobile ? 30 : 180,
//                     left: 0,
//                     bottom: 5,
//                   }}
//                 >
//                   <CartesianGrid
//                     strokeDasharray="2 2"
//                     stroke="#e0e0e0"
//                     horizontal={true}
//                     vertical={false}
//                   />

//                   <YAxis
//                     dataKey="location"
//                     type="category"
//                     axisLine={false}
//                     tickLine={false}
//                     tick={{
//                       fontSize: isMobile ? 8 : 11,
//                       fontWeight: 500,
//                       fill: "#333",
//                       textAnchor: "start",
//                       dx: isMobile ? -80 : -150,
//                     }}
//                     width={isMobile ? 80 : 180}
//                     interval={0}
//                     tickFormatter={(value) => value}
//                   />

//                   <XAxis
//                     type="number"
//                     tick={{ fontSize: isMobile ? 8 : 10 }}
//                     axisLine={false}
//                     tickLine={false}
//                     domain={[0, "dataMax + 5"]}
//                     tickCount={6}
//                   />

//                   <RechartsTooltip
//                     content={<CustomTooltip />}
//                     cursor={{ fill: "rgba(0,0,0,0.03)" }}
//                   />

//                   <Bar
//                     dataKey="real"
//                     name="Present"
//                     fill="#4CAF50"
//                     radius={[0, 10, 10, 0]}
//                   >
//                     {chartData.map((entry, index) => (
//                       <Cell
//                         key={`attendance-cell-${index}`}
//                         fill={
//                           entry.percentage > 70
//                             ? "#4CAF50"
//                             : entry.percentage > 50
//                             ? "#FF9800"
//                             : "#F44336"
//                         }
//                       />
//                     ))}

//                     <LabelList
//                       dataKey="real"
//                       position="insideLeft"
//                       style={{
//                         fontSize: 10,
//                         fontWeight: 700,
//                         fill: "#fff",
//                       }}
//                       offset={12}
//                     />

//                     <LabelList
//                       dataKey="percentage"
//                       position="right"
//                       formatter={(value) => `${value}%`}
//                       style={{
//                         fontSize: 11,
//                         fontWeight: 600,
//                         fill: "#555",
//                       }}
//                       offset={8}
//                     />
//                   </Bar>
//                 </BarChart>
//               </ResponsiveContainer>
//             </Box>

//             {/* Color Legend */}
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: isMobile ? "8px" : "15px",
//                 mt: 2,
//                 flexWrap: "wrap",
//               }}
//             >
//               <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                 <Box
//                   sx={{
//                     width: "14px",
//                     height: "14px",
//                     backgroundColor: "#4CAF50",
//                     borderRadius: "3px",
//                   }}
//                 />
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 600 }}>
//                   High Attendance (&gt;70%)
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                 <Box
//                   sx={{
//                     width: "14px",
//                     height: "14px",
//                     backgroundColor: "#FF9800",
//                     borderRadius: "3px",
//                   }}
//                 />
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 600 }}>
//                   Medium Attendance (50-70%)
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                 <Box
//                   sx={{
//                     width: "14px",
//                     height: "14px",
//                     backgroundColor: "#F44336",
//                     borderRadius: "3px",
//                   }}
//                 />
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 600 }}>
//                   Low Attendance (&lt;50%)
//                 </Typography>
//               </Box>
//             </Box>
//           </Box>
//         )}

//         {/* Data Table */}
//         {loading ? (
//           <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
//             <CircularProgress />
//           </Box>
//         ) : processedData.length > 0 ? (
//           <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto" }}>
//             <Table>
//               <TableHead>
//                 <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
//                   <TableCell sx={{ fontWeight: "bold", fontSize: isMobile ? "11px" : "14px" }}>Location</TableCell>
//                   <TableCell sx={{ fontWeight: "bold", fontSize: isMobile ? "10px" : "14px", textAlign: "center" }}>Total</TableCell>
//                   <TableCell sx={{ fontWeight: "bold", fontSize: isMobile ? "10px" : "14px", textAlign: "center" }}>Present</TableCell>
//                   <TableCell sx={{ fontWeight: "bold", fontSize: isMobile ? "10px" : "14px", textAlign: "center" }}>Absent</TableCell>
//                   <TableCell sx={{ fontWeight: "bold", fontSize: isMobile ? "10px" : "14px", textAlign: "center" }}>%</TableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {processedData.map((row, index) => (
//                   <TableRow key={index} hover>
//                     <TableCell sx={{ fontSize: isMobile ? "11px" : "14px", fontWeight: 500, maxWidth: isMobile ? "120px" : "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
//                       {row.location}
//                     </TableCell>
//                     <TableCell sx={{ textAlign: "center", fontSize: isMobile ? "11px" : "14px" }}>
//                       {row.totalEmp}
//                     </TableCell>
//                     <TableCell sx={{ textAlign: "center", fontSize: isMobile ? "11px" : "14px", color: "#2E7D32", fontWeight: 600 }}>
//                       {row.todayAtt}
//                     </TableCell>
//                     <TableCell sx={{ textAlign: "center", fontSize: isMobile ? "11px" : "14px", color: "#D32F2F", fontWeight: 600 }}>
//                       {row.absent}
//                     </TableCell>
//                     <TableCell sx={{ textAlign: "center", fontSize: isMobile ? "11px" : "14px", fontWeight: 600 }}>
//                       {row.percentage}%
//                     </TableCell>
//                   </TableRow>
//                 ))}
//               </TableBody>
//             </Table>
//           </TableContainer>
//         ) : (
//           <Typography variant="body1" sx={{ textAlign: "center", padding: "40px", color: "#666" }}>
//             No roster attendance data available for today.
//           </Typography>
//         )}
//       </Box>
//     );
//   };

//   export default RosterAttendance;